import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDown,
  ArrowCounterClockwise,
  ArrowLeft,
  ArrowUp,
  BookmarkSimple,
  Check,
  ClockCounterClockwise,
  Copy,
  DotsThreeVertical,
  FloppyDisk,
  ImageSquare,
  MagicWand,
  MusicNotes,
  PencilSimple,
  Plus,
  ShareNetwork,
  Sparkle,
  Trash,
  UploadSimple,
  Warning,
  X,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { DashboardLayout } from '../../components/Layout';
import AIPremiumButton, {
  AI_USAGE_IMAGE_GENERATION,
  AI_USAGE_LUNA_TEXT,
} from '../../components/AIPremiumGate';
import { useAuth } from '../../context/AuthContext';
import api, { getApiErrorMessage } from '../../services/api';
import { resolveMediaUrl } from '../../utils/media';
import { Badge } from '../../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Slider } from '../../components/ui/slider';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import WebInviteRenderer from '../../features/webInvite/WebInviteRenderer';
import { getMissingWebInviteDetails } from '../../features/webInvite/missingDetails';
import {
  WEB_INVITE_MUSIC_TRACKS,
  WEB_INVITE_SECTION_CATALOG,
  WEB_INVITE_TEMPLATES,
  DEFAULT_WEB_INVITE_TEMPLATE_ID,
  cloneInviteConfig,
  completeInviteSections,
  createCustomSection,
  getSectionVariants,
} from '../../features/webInvite/templates';

const HERO_EFFECTS = [
  ['none', 'None'], ['petals', 'Falling petals'], ['hearts', 'Floating hearts'],
  ['snow', 'Soft snow'], ['fireflies', 'Fireflies'], ['golden_dust', 'Golden dust'],
  ['confetti', 'Confetti'], ['sakura', 'Sakura'], ['celestial', 'Celestial stars'], ['diyas', 'Floating diyas'],
];

const FONT_OPTIONS = ['Great Vibes', 'Playfair Display', 'Cormorant Garamond', 'Libre Baskerville', 'DM Serif Display', 'Fraunces', 'Inter', 'Georgia'];
const formatSelectLabel = (value) => value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

const AI_CREATIVITY_OPTIONS = [
  ['refined', 'Refined', 'Elegant and restrained'],
  ['expressive', 'Expressive', 'Personal with a clear point of view'],
  ['adventurous', 'Adventurous', 'Unexpected, layered and playful'],
];

const AI_HERO_STYLES = [
  ['editorial', 'Editorial'], ['watercolor', 'Watercolor'], ['botanical', 'Botanical'],
  ['cinematic', 'Cinematic'], ['minimal', 'Minimal'], ['storybook', 'Storybook'],
  ['heritage', 'Heritage'], ['surreal', 'Surreal'],
];

function sectionDefinition(type) {
  return WEB_INVITE_SECTION_CATALOG.find((item) => item.type === type) || {
    label: 'Custom section',
    description: 'A flexible section for your own message.',
  };
}

function snapshotKey(config, slug) {
  return JSON.stringify({ config, slug });
}

function isValidSlug(value) {
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value) && value.length >= 3 && value.length <= 80;
}

function readableRevisionDate(value) {
  if (!value) return 'Date unavailable';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Date unavailable';
  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function templateNameWithoutEvent(name, eventName) {
  const fallback = name || 'My invitation';
  const suffix = eventName ? ` - ${eventName}` : '';
  return suffix && fallback.endsWith(suffix)
    ? fallback.slice(0, -suffix.length)
    : fallback;
}

function nextTemplateCopyName(baseName, templates) {
  const root = (baseName || 'My invitation').replace(/\s+Copy(?:\s+\d+)?$/i, '').trim() || 'My invitation';
  const existingNames = new Set(templates.map((item) => String(item.name || '').trim().toLowerCase()));
  for (let copyNumber = 1; copyNumber < 1000; copyNumber += 1) {
    const suffix = copyNumber === 1 ? ' Copy' : ` Copy ${copyNumber}`;
    const candidate = `${root.slice(0, Math.max(1, 80 - suffix.length))}${suffix}`;
    if (!existingNames.has(candidate.toLowerCase())) return candidate;
  }
  return `${root.slice(0, 70)} Copy`;
}

function guestDisplayName(guest) {
  return guest?.name || guest?.email || guest?.phone || 'Unnamed guest';
}

function TemplatePreview({ template }) {
  const layout = template.config.layout || {};
  const artwork = layout.background_image_url;
  const frame = layout.show_frame && layout.frame_image_url;
  const kashiPortal = template.id === 'galago.kashi-riverlight' && frame;
  const birthdayPreview = template.eventTypes?.includes('birthday');
  return (
      <div
      className="relative h-28 overflow-hidden rounded-lg border sm:h-32"
      style={{
        background: artwork
          ? `url(${resolveMediaUrl(artwork)}) ${layout.background_position || 'center'}/cover`
          : template.config.theme.background_color,
      }}
    >
      <div className="absolute inset-0" style={{ background: artwork ? `linear-gradient(180deg, ${template.config.theme.background_color}22, ${template.config.theme.primary_color}44)` : `linear-gradient(145deg, ${template.config.theme.primary_color}, ${template.config.theme.text_color})` }} />
      {kashiPortal ? (
        <div
          className="pointer-events-none absolute inset-1 z-[1] border-solid"
          style={{
            borderImageSource: `url(${resolveMediaUrl(frame)})`,
            borderImageSlice: '25% 12% 11% 12%',
            borderImageWidth: 1,
            borderImageRepeat: 'stretch',
            borderTopWidth: '28px',
            borderRightWidth: '18px',
            borderBottomWidth: '18px',
            borderLeftWidth: '18px',
            mixBlendMode: 'multiply',
          }}
          aria-hidden="true"
        />
      ) : frame && <img src={resolveMediaUrl(frame)} alt="" className="pointer-events-none absolute left-1/2 top-1/2 z-[1] h-[118%] w-auto max-w-none -translate-x-1/2 -translate-y-1/2 object-contain" />}
      <div className={`absolute inset-x-5 top-7 z-[2] text-center ${artwork ? '' : 'text-white'}`} style={artwork ? { color: template.config.theme.primary_color } : {}}>
        <div className="mx-auto h-px w-10 bg-white/60" />
        <p className="mt-2 text-sm" style={{ fontFamily: `${template.config.theme.heading_font}, serif` }}>{birthdayPreview ? '60' : 'A & K'}</p>
        <p className="mt-1 text-[0.38rem] uppercase tracking-[0.26em] opacity-70">{birthdayPreview ? 'Surprise birthday' : 'You are invited'}</p>
      </div>
      <div className="absolute inset-x-4 bottom-3 z-[2] grid grid-cols-3 gap-1.5">
        {template.swatches.map((color) => <span key={color} className="h-6 rounded border border-black/5" style={{ background: color }} />)}
      </div>
    </div>
  );
}

function SavedTemplatePlaceholder({ onBrowse, bare = false }) {
  const content = (
    <div className="flex flex-col items-center px-5 py-8 text-center text-slate-500">
      <div className="grid h-12 w-12 place-items-center rounded-full bg-slate-200/75">
        <BookmarkSimple className="h-6 w-6 text-slate-400" weight="light" />
      </div>
      <p className="mt-4 max-w-52 text-sm font-medium leading-6 text-slate-600">Designs you save will appear here.</p>
      <Button type="button" variant="outline" size="sm" className="mt-4 border-slate-300 bg-white/70" onClick={onBrowse}>Browse templates</Button>
    </div>
  );

  if (bare) {
    return <div className="col-span-2 grid min-h-64 place-items-center">{content}</div>;
  }

  return (
    <Card className="overflow-hidden border-dashed border-slate-300 bg-slate-100/70">
      <CardContent className="grid min-h-64 place-items-center p-0">{content}</CardContent>
    </Card>
  );
}

function InvitePhotoPicker({ title, description, photos = [], selectedUrls = [], multiple = false, uploading = false, onToggle, onUpload, onClear }) {
  const choices = [...new Set([...selectedUrls, ...photos].filter(Boolean))];
  return (
    <div className="space-y-3 rounded-lg border p-3">
      <div>
        <p className="text-sm font-medium">{title}</p>
        {description && <p className="mt-0.5 text-xs leading-5 text-muted-foreground">{description}</p>}
      </div>
      {choices.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {choices.map((url, index) => {
            const selected = selectedUrls.includes(url);
            return (
              <button
                key={`${url}-${index}`}
                type="button"
                onClick={() => onToggle?.(url)}
                className={`relative aspect-square overflow-hidden rounded-lg border-2 transition-all ${selected ? 'border-primary ring-2 ring-primary/15' : 'border-transparent opacity-60 hover:opacity-100'}`}
                aria-label={`${selected ? 'Remove' : 'Use'} photo ${index + 1}`}
              >
                <img src={resolveMediaUrl(url)} alt="" className="h-full w-full object-cover" />
                {selected && <span className="absolute right-1.5 top-1.5 grid h-5 w-5 place-items-center rounded-full bg-primary text-primary-foreground shadow"><Check className="h-3 w-3" weight="bold" /></span>}
              </button>
            );
          })}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <label className={`inline-flex cursor-pointer items-center rounded-md border border-dashed px-3 py-2 text-xs font-medium transition-colors hover:bg-muted/40 ${uploading ? 'pointer-events-none opacity-60' : ''}`}>
          <UploadSimple className="mr-1.5 h-4 w-4" />{uploading ? 'Uploading...' : multiple ? 'Upload another photo' : 'Upload a photo'}
          <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="sr-only" disabled={uploading} onChange={onUpload} />
        </label>
        {selectedUrls.length > 0 && onClear && <Button type="button" size="sm" variant="ghost" onClick={onClear}>Use event default</Button>}
      </div>
      {!choices.length && <p className="text-xs text-muted-foreground">Upload a photo, or add photos to the event gallery first.</p>}
    </div>
  );
}

function SectionInsertButton({ placement, onClick }) {
  return (
    <button type="button" onClick={onClick} className="group flex w-full items-center gap-3 py-1 text-muted-foreground" aria-label={`Add a section ${placement}`}>
      <span className="h-px flex-1 bg-border transition-colors group-hover:bg-primary/40" />
      <span className="inline-flex min-h-9 items-center gap-1.5 rounded-full border bg-background px-3 text-xs font-medium shadow-sm transition-all group-hover:border-primary group-hover:text-primary">
        <Plus className="h-4 w-4" weight="bold" />
        Add section {placement}
      </span>
      <span className="h-px flex-1 bg-border transition-colors group-hover:bg-primary/40" />
    </button>
  );
}

function EmptyState() {
  return (
    <DashboardLayout>
      <div className="mx-auto max-w-xl py-16 text-center">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-primary/10 text-primary">
          <Sparkle className="h-7 w-7" />
        </div>
        <h1 className="mt-5 font-serif text-3xl">Select an event first</h1>
        <p className="mt-3 text-sm leading-6 text-muted-foreground">
          Choose an event from the event switcher to create its web invite.
        </p>
      </div>
    </DashboardLayout>
  );
}

export default function WebInviteBuilder() {
  const { currentEvent } = useAuth();
  const [config, setConfig] = useState(null);
  const [invite, setInvite] = useState(null);
  const [slug, setSlug] = useState('');
  const [selectedSectionId, setSelectedSectionId] = useState('hero');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saveState, setSaveState] = useState('idle');
  const [aiStudioOpen, setAiStudioOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiCreativity, setAiCreativity] = useState('expressive');
  const [aiPreservePalette, setAiPreservePalette] = useState(false);
  const [aiConcepts, setAiConcepts] = useState([]);
  const [selectedAIConceptId, setSelectedAIConceptId] = useState(null);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiHeroStyle, setAiHeroStyle] = useState('editorial');
  const [aiHeroGenerating, setAiHeroGenerating] = useState(false);
  const [aiHeroAsset, setAiHeroAsset] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [revisions, setRevisions] = useState([]);
  const [selectedRevisionNumber, setSelectedRevisionNumber] = useState(null);
  const [pendingRestoreRevision, setPendingRestoreRevision] = useState(null);
  const [restoringRevision, setRestoringRevision] = useState(false);
  const [workspaceView, setWorkspaceView] = useState('templates');
  const [mobileEditorOpen, setMobileEditorOpen] = useState(false);
  const [mobileEditorExpanded, setMobileEditorExpanded] = useState(false);
  const [mobileEditorCategory, setMobileEditorCategory] = useState('content');
  const [mobileOpeningEditorOnly, setMobileOpeningEditorOnly] = useState(false);
  const [mobilePreviewShowingOpening, setMobilePreviewShowingOpening] = useState(true);
  const [editorPreviewInitiallyOpened, setEditorPreviewInitiallyOpened] = useState(false);
  const [openingSettingsOpen, setOpeningSettingsOpen] = useState(false);
  const [hasEditorChanges, setHasEditorChanges] = useState(false);
  const [previewTemplateId, setPreviewTemplateId] = useState(null);
  const [previewSavedTemplateId, setPreviewSavedTemplateId] = useState(null);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [savedTemplatesLoading, setSavedTemplatesLoading] = useState(false);
  const [templateCollectionTab, setTemplateCollectionTab] = useState('library');
  const [activeSavedTemplateId, setActiveSavedTemplateId] = useState(null);
  const [activeGuestTemplateId, setActiveGuestTemplateId] = useState(null);
  const [usingLibraryTemplateId, setUsingLibraryTemplateId] = useState(null);
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);
  const [saveTemplateName, setSaveTemplateName] = useState('');
  const [saveTemplatePlaceholder, setSaveTemplatePlaceholder] = useState('My invitation');
  const [pendingTemplateSave, setPendingTemplateSave] = useState(null);
  const [savingNamedTemplate, setSavingNamedTemplate] = useState(false);
  const [duplicateTemplatePrompt, setDuplicateTemplatePrompt] = useState(null);
  const [duplicateSaveMode, setDuplicateSaveMode] = useState('copy');
  const [duplicateTemplateNames, setDuplicateTemplateNames] = useState({ replace: '', copy: '' });
  const [resolvingDuplicateTemplate, setResolvingDuplicateTemplate] = useState(false);
  const [pendingDeleteTemplate, setPendingDeleteTemplate] = useState(null);
  const [deletingSavedTemplate, setDeletingSavedTemplate] = useState(false);
  const [shareTemplate, setShareTemplate] = useState(null);
  const [pendingShareAction, setPendingShareAction] = useState(null);
  const [checkingShareAction, setCheckingShareAction] = useState(false);
  const [shareGuests, setShareGuests] = useState([]);
  const [shareGuestsLoading, setShareGuestsLoading] = useState(false);
  const [shareGuestSearch, setShareGuestSearch] = useState('');
  const [selectedShareGuestIds, setSelectedShareGuestIds] = useState([]);
  const [sendingShare, setSendingShare] = useState(false);
  const [uploadingMusic, setUploadingMusic] = useState(false);
  const [uploadingInviteImageSlot, setUploadingInviteImageSlot] = useState(null);
  const [previewScrollTarget, setPreviewScrollTarget] = useState(null);
  const [sectionInsertRequest, setSectionInsertRequest] = useState(null);
  const currentEventType = currentEvent?.event_type || 'wedding';
  const libraryTemplates = useMemo(
    () => WEB_INVITE_TEMPLATES.filter((template) => (
      !template.eventTypes || template.eventTypes.includes(currentEventType)
    )),
    [currentEventType],
  );

  const configRef = useRef(null);
  const slugRef = useRef('');
  const inviteRef = useRef(null);
  const baselineRef = useRef('');
  const saveInFlightRef = useRef(null);
  const eventIdRef = useRef(null);
  const saveStateRef = useRef('idle');
  const mobileEditorDirtyRef = useRef(false);
  const mobileSaveNoticeShownRef = useRef(false);
  const mobileTemplateSaveInFlightRef = useRef(null);
  const editorOptionsScrollRef = useRef(null);
  const sectionEditorRef = useRef(null);
  const previewScrollRequestRef = useRef(0);
  const previewScrollLockRef = useRef(null);
  const previewScrollUnlockTimerRef = useRef(null);

  const updateSaveState = useCallback((nextState) => {
    saveStateRef.current = nextState;
    setSaveState(nextState);
  }, []);

  const setConfigAndRef = useCallback((nextValue, { markEditorDirty = true } = {}) => {
    const next = typeof nextValue === 'function' ? nextValue(configRef.current) : nextValue;
    configRef.current = next;
    setConfig(next);
    if (markEditorDirty) {
      mobileEditorDirtyRef.current = true;
      setHasEditorChanges(true);
    }
    if (saveStateRef.current !== 'conflict') updateSaveState('pending');
  }, [updateSaveState]);

  const applyManagementResponse = useCallback((payload, { establishBaseline = false } = {}) => {
    const nextInvite = payload.invite;
    inviteRef.current = nextInvite;
    setInvite(nextInvite);
    if (establishBaseline) {
      const nextConfig = completeInviteSections(nextInvite.draft_config);
      const nextSlug = nextInvite.slug;
      configRef.current = nextConfig;
      slugRef.current = nextSlug;
      setConfig(nextConfig);
      setSlug(nextSlug);
      baselineRef.current = snapshotKey(nextConfig, nextSlug);
    }
  }, []);

  useEffect(() => {
    if (!currentEvent?.id) {
      setLoading(false);
      setConfig(null);
      return undefined;
    }

    let active = true;
    const loadInvite = async () => {
      setLoading(true);
      setLoadError('');
      setAiStudioOpen(false);
      setAiPrompt('');
      setAiConcepts([]);
      setSelectedAIConceptId(null);
      setAiHeroAsset(null);
      setHistoryOpen(false);
      setRevisions([]);
      setSelectedRevisionNumber(null);
      setPendingRestoreRevision(null);
      setWorkspaceView('templates');
      setMobileEditorOpen(false);
      setMobileEditorExpanded(false);
      setMobileEditorCategory('content');
      setMobileOpeningEditorOnly(false);
      setMobilePreviewShowingOpening(true);
      setEditorPreviewInitiallyOpened(false);
      setOpeningSettingsOpen(false);
      setHasEditorChanges(false);
      mobileSaveNoticeShownRef.current = false;
      mobileEditorDirtyRef.current = false;
      mobileTemplateSaveInFlightRef.current = null;
      setPreviewTemplateId(currentEventType === 'birthday' ? null : DEFAULT_WEB_INVITE_TEMPLATE_ID);
      setPreviewSavedTemplateId(null);
      setSavedTemplates([]);
      setSavedTemplatesLoading(true);
      setTemplateCollectionTab('library');
      setActiveSavedTemplateId(null);
      setActiveGuestTemplateId(null);
      setUsingLibraryTemplateId(null);
      setSaveTemplateDialogOpen(false);
      setPendingTemplateSave(null);
      setDuplicateTemplatePrompt(null);
      setDuplicateSaveMode('copy');
      setDuplicateTemplateNames({ replace: '', copy: '' });
      setResolvingDuplicateTemplate(false);
      setPendingDeleteTemplate(null);
      setShareTemplate(null);
      setPendingShareAction(null);
      setShareGuests([]);
      setShareGuestSearch('');
      setSelectedShareGuestIds([]);
      setPreviewScrollTarget(null);
      setSectionInsertRequest(null);
      setUploadingInviteImageSlot(null);
      previewScrollLockRef.current = null;
      window.clearTimeout(previewScrollUnlockTimerRef.current);
      previewScrollUnlockTimerRef.current = null;
      updateSaveState('idle');
      eventIdRef.current = currentEvent.id;
      try {
        const response = await api.get(`/events/${currentEvent.id}/web-invite`);
        if (!active || eventIdRef.current !== currentEvent.id) return;
        applyManagementResponse(response.data, { establishBaseline: true });
        setSelectedSectionId('hero');
        try {
          const savedResponse = await api.get(`/events/${currentEvent.id}/web-invite/saved`);
          if (!active || eventIdRef.current !== currentEvent.id) return;
          const items = (savedResponse.data?.items || []).map((item) => ({
            ...item,
            name: templateNameWithoutEvent(item.name, currentEvent.name),
          }));
          setSavedTemplates(items);
          setActiveGuestTemplateId(items.find((item) => item.is_active)?.id || null);
          setTemplateCollectionTab(items.length ? 'mine' : 'library');
          setPreviewSavedTemplateId(items[0]?.id || null);
        } catch (_savedError) {
          if (active) setSavedTemplates([]);
        }
      } catch (error) {
        if (!active) return;
        setLoadError(getApiErrorMessage(error, 'Could not load this web invite.'));
      } finally {
        if (active) {
          setLoading(false);
          setSavedTemplatesLoading(false);
        }
      }
    };
    loadInvite();
    return () => { active = false; };
  }, [applyManagementResponse, currentEvent?.id, currentEvent?.name, currentEventType, updateSaveState]);

  const persistDraft = useCallback(async ({ announce = false } = {}) => {
    if (!currentEvent?.id || !configRef.current || !inviteRef.current) return null;
    if (saveStateRef.current === 'conflict') return null;
    if (!isValidSlug(slugRef.current)) {
      if (announce) toast.error('The invite address needs at least 3 letters or numbers.');
      return null;
    }
    const requestEventId = currentEvent.id;

    if (saveInFlightRef.current) await saveInFlightRef.current;
    if (eventIdRef.current !== requestEventId) return null;

    const savedConfig = cloneInviteConfig(configRef.current);
    const savedSlug = slugRef.current;
    const savedKey = snapshotKey(savedConfig, savedSlug);
    if (savedKey === baselineRef.current) return { invite: inviteRef.current };

    updateSaveState('saving');
    const request = api.put(`/events/${currentEvent.id}/web-invite/draft`, {
      config: savedConfig,
      slug: savedSlug,
      expected_revision: inviteRef.current.revision,
    });
    saveInFlightRef.current = request;

    try {
      const response = await request;
      if (eventIdRef.current !== requestEventId) return null;
      applyManagementResponse(response.data);
      baselineRef.current = savedKey;
      updateSaveState(
        snapshotKey(configRef.current, slugRef.current) === savedKey ? 'saved' : 'pending',
      );
      if (announce) toast.success('Invite draft saved');
      return response.data;
    } catch (error) {
      if (error.response?.status === 409) {
        updateSaveState('conflict');
        toast.error('This invite changed in another session. Reload before continuing.');
      } else {
        updateSaveState('error');
        if (announce) toast.error(getApiErrorMessage(error, 'Could not save the invite draft.'));
      }
      throw error;
    } finally {
      saveInFlightRef.current = null;
    }
  }, [applyManagementResponse, currentEvent?.id, updateSaveState]);

  useEffect(() => {
    if (!config || !invite || ['conflict', 'error'].includes(saveStateRef.current)) return undefined;
    if (snapshotKey(config, slug) === baselineRef.current) return undefined;
    if (!isValidSlug(slug)) return undefined;
    const timer = window.setTimeout(() => {
      persistDraft().catch(() => {});
    }, 900);
    return () => window.clearTimeout(timer);
  }, [config, invite, persistDraft, slug]);

  const sortedSections = useMemo(
    () => [...(config?.sections || [])].sort((a, b) => a.order - b.order),
    [config?.sections],
  );
  const visibleSections = sortedSections.filter((section) => section.enabled);
  const selectedSection = visibleSections.find((section) => section.id === selectedSectionId) || visibleSections[0] || sortedSections[0];
  const availableInvitePhotos = useMemo(() => {
    const experience = currentEvent?.guest_experience || {};
    const heroMedia = (experience.hero_media || []).map((item) => item?.url).filter(Boolean);
    return [...new Set([
      experience.couple_photo_url,
      ...(currentEvent?.wedding_photos_preview || []),
      ...heroMedia,
    ].filter((url) => typeof url === 'string' && url))];
  }, [currentEvent?.guest_experience, currentEvent?.wedding_photos_preview]);

  const scrollSectionEditorIntoView = useCallback(() => {
    window.requestAnimationFrame(() => {
      const container = editorOptionsScrollRef.current;
      const editor = sectionEditorRef.current;
      if (!container || !editor) return;
      const containerRect = container.getBoundingClientRect();
      const editorRect = editor.getBoundingClientRect();
      const top = container.scrollTop + editorRect.top - containerRect.top - 12;
      container.scrollTo({ top, behavior: 'smooth' });
    });
  }, []);

  const handlePreviewVisibleSection = useCallback((sectionId) => {
    if (!sectionId) return;
    if (previewScrollLockRef.current && previewScrollLockRef.current !== sectionId) return;
    if (previewScrollLockRef.current === sectionId) {
      previewScrollLockRef.current = null;
      window.clearTimeout(previewScrollUnlockTimerRef.current);
      previewScrollUnlockTimerRef.current = null;
    }
    setSelectedSectionId(sectionId);
    scrollSectionEditorIntoView();
  }, [scrollSectionEditorIntoView]);

  useEffect(() => () => window.clearTimeout(previewScrollUnlockTimerRef.current), []);

  const updateConfig = (path, value) => {
    setConfigAndRef((current) => {
      const next = cloneInviteConfig(current);
      const segments = path.split('.');
      let target = next;
      segments.slice(0, -1).forEach((segment) => { target = target[segment]; });
      target[segments[segments.length - 1]] = value;
      return next;
    });
  };

  const uploadInviteMusic = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || uploadingMusic) return;
    const formData = new FormData();
    formData.append('file', file);
    setUploadingMusic(true);
    try {
      const response = await api.post(
        `/events/${currentEvent.id}/web-invite/music`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setConfigAndRef((current) => ({
        ...current,
        music: {
          ...current.music,
          enabled: true,
          track_id: 'custom',
          custom_audio_url: response.data.url,
          custom_audio_name: response.data.name || file.name,
        },
      }));
      toast.success(`${response.data.name || file.name} added to this invitation`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not upload this audio file.'));
    } finally {
      setUploadingMusic(false);
    }
  };

  const updateSection = (sectionId, updater) => {
    setConfigAndRef((current) => ({
      ...current,
      sections: current.sections.map((section) => (
        section.id === sectionId ? updater(cloneInviteConfig(section)) : section
      )),
    }));
  };

  const uploadInviteImage = async (event, slot, applyUrl) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || uploadingInviteImageSlot) return;
    const formData = new FormData();
    formData.append('file', file);
    setUploadingInviteImageSlot(slot);
    try {
      const response = await api.post(
        `/events/${currentEvent.id}/web-invite/images`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      applyUrl(response.data.url);
      toast.success(`${response.data.name || file.name} added to this invitation`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not upload this image.'));
    } finally {
      setUploadingInviteImageSlot(null);
    }
  };

  const moveSection = (sectionId, direction) => {
    setConfigAndRef((current) => {
      const ordered = [...current.sections].sort((a, b) => a.order - b.order);
      const visible = ordered.filter((section) => section.enabled);
      const hidden = ordered.filter((section) => !section.enabled);
      const index = visible.findIndex((section) => section.id === sectionId);
      const destination = index + direction;
      if (index < 0 || destination < 0 || destination >= visible.length) return current;
      [visible[index], visible[destination]] = [visible[destination], visible[index]];
      return { ...current, sections: [...visible, ...hidden].map((section, order) => ({ ...section, order })) };
    });
  };

  const loadTemplateForEditing = (template, { savedTemplateId = null, announce = true } = {}) => {
    if (!template) return;
    const nextConfig = completeInviteSections(template.config);
    setConfigAndRef(nextConfig, { markEditorDirty: false });
    mobileEditorDirtyRef.current = false;
    setHasEditorChanges(false);
    setActiveSavedTemplateId(savedTemplateId);
    setSelectedSectionId('hero');
    setEditorPreviewInitiallyOpened(false);
    if (announce) toast.success(`${template.name} is ready to personalize`);
  };

  const openTemplatePreview = (template) => {
    setPreviewTemplateId(template.id);
    setPreviewSavedTemplateId(null);
    setSelectedSectionId('hero');
    setMobilePreviewShowingOpening(template.config?.opening?.enabled !== false);
    if (!window.matchMedia('(min-width: 1280px)').matches) setWorkspaceView('preview');
  };

  const openSavedTemplatePreview = (template) => {
    setPreviewTemplateId(null);
    setPreviewSavedTemplateId(template.id);
    setSelectedSectionId('hero');
    setMobilePreviewShowingOpening(template.config?.opening?.enabled !== false);
    if (!window.matchMedia('(min-width: 1280px)').matches) setWorkspaceView('preview');
  };

  const openCurrentPreview = () => {
    setPreviewTemplateId(null);
    setPreviewSavedTemplateId(null);
    setMobileEditorOpen(false);
    setWorkspaceView('preview');
  };

  const openMobileEditor = (category = 'content', sectionId = null) => {
    if (!mobileEditorOpen) setMobileEditorExpanded(false);
    setMobileEditorCategory(category);
    setMobileOpeningEditorOnly(false);
    setOpeningSettingsOpen(false);
    if (sectionId) {
      setSelectedSectionId(sectionId);
    }
    setMobileEditorOpen(true);
    window.requestAnimationFrame(() => editorOptionsScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' }));
  };

  const openMobileOpeningEditor = () => {
    if (!mobileEditorOpen) setMobileEditorExpanded(false);
    setMobileEditorCategory('content');
    setMobileOpeningEditorOnly(true);
    setOpeningSettingsOpen(true);
    setMobileEditorOpen(true);
    window.requestAnimationFrame(() => editorOptionsScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' }));
  };

  const handleMobileEditorScroll = (event) => {
    if (window.matchMedia('(min-width: 1280px)').matches) return;
    if (event.currentTarget.scrollTop > 12) setMobileEditorExpanded(true);
  };

  const editPreviewedTemplate = () => {
    const template = libraryTemplates.find((item) => item.id === previewTemplateId);
    const savedTemplate = savedTemplates.find((item) => item.id === previewSavedTemplateId);
    const templateConfig = template?.config || savedTemplate?.config;
    const showOpeningEditor = templateConfig?.opening?.enabled !== false && mobilePreviewShowingOpening;
    const previewedSectionId = templateConfig?.sections?.some((section) => section.enabled && section.id === selectedSectionId)
      ? selectedSectionId
      : templateConfig?.sections?.find((section) => section.enabled)?.id || 'hero';
    if (template) {
      loadTemplateForEditing(template, { announce: false });
      setActiveSavedTemplateId(null);
      mobileSaveNoticeShownRef.current = false;
    }
    if (savedTemplate) {
      loadTemplateForEditing(
        { name: savedTemplate.name, config: savedTemplate.config },
        { savedTemplateId: savedTemplate.id },
      );
      mobileSaveNoticeShownRef.current = true;
    }
    setPreviewTemplateId(null);
    setPreviewSavedTemplateId(null);
    setSelectedSectionId(previewedSectionId);
    setEditorPreviewInitiallyOpened(!showOpeningEditor);
    setMobileEditorExpanded(false);
    setMobileOpeningEditorOnly(showOpeningEditor);
    setOpeningSettingsOpen(showOpeningEditor);
    setMobileEditorOpen(true);
    setMobileEditorCategory('content');
    setWorkspaceView('editor');
    if (!showOpeningEditor) {
      previewScrollRequestRef.current += 1;
      setPreviewScrollTarget({ id: previewedSectionId, request: previewScrollRequestRef.current });
    }
  };

  const makePreviewActiveForGuests = async () => {
    const template = libraryTemplates.find((item) => item.id === previewTemplateId);
    const savedTemplate = savedTemplates.find((item) => item.id === previewSavedTemplateId);
    if (template) await chooseLibraryTemplate(template);
    else if (savedTemplate) await chooseSavedTemplate(savedTemplate);
    else setWorkspaceView('editor');
  };

  const insertSection = (sectionId = 'custom') => {
    if (!sectionInsertRequest || !selectedSection) return;
    if (sectionId === 'custom' && config.sections.length >= 20) {
      toast.error('An invite can contain up to 20 sections.');
      return;
    }
    let insertedId = sectionId;
    setConfigAndRef((current) => {
      const ordered = [...current.sections].sort((a, b) => a.order - b.order);
      const existing = ordered.find((section) => section.id === sectionId);
      const inserted = existing
        ? { ...existing, enabled: true }
        : createCustomSection(ordered.length);
      insertedId = inserted.id;
      const visible = ordered.filter((section) => section.enabled && section.id !== inserted.id);
      const hidden = ordered.filter((section) => !section.enabled && section.id !== inserted.id);
      const anchorIndex = Math.max(0, visible.findIndex((section) => section.id === sectionInsertRequest.anchorId));
      const destination = sectionInsertRequest.placement === 'before' ? anchorIndex : anchorIndex + 1;
      visible.splice(destination, 0, inserted);
      return { ...current, sections: [...visible, ...hidden].map((section, order) => ({ ...section, order })) };
    });
    setSelectedSectionId(insertedId);
    setSectionInsertRequest(null);
    window.requestAnimationFrame(() => {
      previewScrollLockRef.current = insertedId;
      window.clearTimeout(previewScrollUnlockTimerRef.current);
      previewScrollUnlockTimerRef.current = window.setTimeout(() => {
        if (previewScrollLockRef.current === insertedId) previewScrollLockRef.current = null;
      }, 1400);
      previewScrollRequestRef.current += 1;
      setPreviewScrollTarget({ id: insertedId, request: previewScrollRequestRef.current });
      scrollSectionEditorIntoView();
    });
  };

  const removeSectionFromInvite = (section) => {
    if (!section) return;
    if (visibleSections.length <= 1) {
      toast.error('Keep at least one section in the invitation.');
      return;
    }
    if (section.type === 'custom') {
      deleteCustomSection(section.id);
      return;
    }
    const visible = sortedSections.filter((item) => item.enabled);
    const index = visible.findIndex((item) => item.id === section.id);
    const nextSelection = visible[index + 1] || visible[index - 1] || sortedSections.find((item) => item.id === 'hero');
    setConfigAndRef((current) => {
      const ordered = [...current.sections].sort((a, b) => a.order - b.order);
      const remainingVisible = ordered.filter((item) => item.enabled && item.id !== section.id);
      const removed = ordered.find((item) => item.id === section.id);
      const hidden = ordered.filter((item) => !item.enabled && item.id !== section.id);
      return {
        ...current,
        sections: [...remainingVisible, ...hidden, { ...removed, enabled: false }].filter(Boolean).map((item, order) => ({ ...item, order })),
      };
    });
    if (nextSelection) setSelectedSectionId(nextSelection.id);
  };

  const deleteCustomSection = (sectionId) => {
    const fallbackSection = visibleSections.find((section) => section.id !== sectionId);
    setConfigAndRef((current) => ({
      ...current,
      sections: current.sections
        .filter((section) => section.id !== sectionId)
        .sort((a, b) => a.order - b.order)
        .map((section, order) => ({ ...section, order })),
    }));
    if (fallbackSection) setSelectedSectionId(fallbackSection.id);
  };

  const savedNamePlaceholder = (baseName = 'My invitation') => (
    templateNameWithoutEvent(baseName, currentEvent?.name).slice(0, 80)
  );

  const openSavedTemplateDialog = ({ sourceConfig, sourceName, mode = 'save_as' }) => {
    setSaveTemplateName('');
    setSaveTemplatePlaceholder(savedNamePlaceholder(sourceName));
    setPendingTemplateSave({
      mode,
      config: cloneInviteConfig(sourceConfig),
    });
    setSaveTemplateDialogOpen(true);
  };

  const createNamedTemplate = async () => {
    if (!pendingTemplateSave || savingNamedTemplate) return;
    const name = (saveTemplateName.trim() || saveTemplatePlaceholder).slice(0, 80);
    setSavingNamedTemplate(true);
    try {
      const response = await api.post(`/events/${currentEvent.id}/web-invite/saved`, {
        name,
        config: pendingTemplateSave.config,
      });
      const created = response.data;
      setSavedTemplates((current) => [created, ...current.filter((item) => item.id !== created.id)]);
      setTemplateCollectionTab('mine');
      if (pendingTemplateSave.mode !== 'library') {
        setActiveSavedTemplateId(created.id);
        if (JSON.stringify(configRef.current) === JSON.stringify(pendingTemplateSave.config)) {
          mobileEditorDirtyRef.current = false;
        }
        mobileSaveNoticeShownRef.current = true;
      }
      setSaveTemplateDialogOpen(false);
      setPendingTemplateSave(null);
      toast.success(`${created.name} added to My templates`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not save this invitation.'));
    } finally {
      setSavingNamedTemplate(false);
    }
  };

  const saveCurrentNamedTemplate = async ({ announce = true } = {}) => {
    try {
      await flushDraft();
      const existing = savedTemplates.find((item) => item.id === activeSavedTemplateId);
      if (!existing) {
        openSavedTemplateDialog({
          sourceConfig: configRef.current,
          sourceName: WEB_INVITE_TEMPLATES.find((item) => item.id === configRef.current?.template_id)?.name || 'My invitation',
        });
        return null;
      }
      setSavingNamedTemplate(true);
      const templateConfig = cloneInviteConfig(configRef.current);
      const templateConfigKey = JSON.stringify(templateConfig);
      const response = await api.put(
        `/events/${currentEvent.id}/web-invite/saved/${existing.id}`,
        {
          name: existing.name,
          config: templateConfig,
          expected_revision: existing.revision,
        },
      );
      const updated = response.data;
      setSavedTemplates((current) => [{ ...updated, is_active: updated.id === activeGuestTemplateId }, ...current.filter((item) => item.id !== updated.id)]);
      if (JSON.stringify(configRef.current) === templateConfigKey) mobileEditorDirtyRef.current = false;
      if (announce) toast.success(`${updated.name} saved`);
      return updated;
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not save this invitation.'));
      return null;
    } finally {
      setSavingNamedTemplate(false);
    }
  };

  const saveCurrentAsTemplate = async () => {
    try {
      await flushDraft();
      const existing = savedTemplates.find((item) => item.id === activeSavedTemplateId);
      openSavedTemplateDialog({
        sourceConfig: configRef.current,
        sourceName: existing?.name || WEB_INVITE_TEMPLATES.find((item) => item.id === configRef.current?.template_id)?.name || 'My invitation',
      });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Save the current draft before creating a copy.'));
    }
  };

  const performCopySavedTemplateLink = async (template) => {
    try {
      await navigator.clipboard.writeText(template.public_url);
      toast.success(`${template.name} link copied`);
    } catch (_error) {
      toast.error('Could not copy the invite link');
    }
  };

  const performShareSavedTemplate = async (template) => {
    setShareTemplate(template);
    setShareGuests([]);
    setShareGuestSearch('');
    setSelectedShareGuestIds([]);
    setShareGuestsLoading(true);
    try {
      const allGuests = [];
      let skip = 0;
      let hasMore = true;
      while (hasMore) {
        const response = await api.get(`/events/${currentEvent.id}/guests/list`, {
          params: { skip, limit: 200 },
        });
        const page = Array.isArray(response.data) ? response.data : [];
        allGuests.push(...page);
        skip += page.length;
        hasMore = response.headers['x-has-more'] === 'true' && page.length > 0;
      }
      setShareGuests(allGuests);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not load the guest list.'));
    } finally {
      setShareGuestsLoading(false);
    }
  };

  const checkBeforeSharing = async (template, action) => {
    if (template.id !== activeGuestTemplateId || checkingShareAction) return;
    setCheckingShareAction(true);
    try {
      const response = await api.get(`/public/web-invites/${template.slug}`);
      const missing = getMissingWebInviteDetails(response.data);
      if (missing.length) {
        setPendingShareAction({ template, action, missing });
      } else if (action === 'copy') {
        await performCopySavedTemplateLink(template);
      } else {
        await performShareSavedTemplate(template);
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not check this invitation. Please try again.'));
    } finally {
      setCheckingShareAction(false);
    }
  };

  const copySavedTemplateLink = (template) => checkBeforeSharing(template, 'copy');
  const shareSavedTemplate = (template) => checkBeforeSharing(template, 'share');

  const continueShareAction = async () => {
    if (!pendingShareAction) return;
    const { template, action } = pendingShareAction;
    setPendingShareAction(null);
    if (action === 'copy') await performCopySavedTemplateLink(template);
    else await performShareSavedTemplate(template);
  };

  const customizePendingTemplate = () => {
    if (!pendingShareAction) return;
    const { template, missing } = pendingShareAction;
    const firstSection = (template.config?.sections || []).find((section) =>
      missing.some((item) => item.toLowerCase().startsWith(sectionDefinition(section.type).label.toLowerCase()))
    );
    setPendingShareAction(null);
    loadTemplateForEditing(template, { savedTemplateId: template.id });
    mobileSaveNoticeShownRef.current = true;
    if (firstSection) setSelectedSectionId(firstSection.id);
    setPreviewTemplateId(null);
    setPreviewSavedTemplateId(null);
    setMobileEditorExpanded(false);
    setMobileOpeningEditorOnly(false);
    setOpeningSettingsOpen(false);
    setMobileEditorOpen(true);
    setMobileEditorCategory('content');
    setWorkspaceView('editor');
  };

  const activateSavedTemplate = async (template) => {
    try {
      await api.post(`/events/${currentEvent.id}/web-invite/saved/${template.id}/activate`);
      setActiveGuestTemplateId(template.id);
      setSavedTemplates((items) => items.map((item) => ({ ...item, is_active: item.id === template.id })));
      toast.success(`${template.name} is now the active Web Invite`);
      return true;
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not activate this Web Invite.'));
      return false;
    }
  };

  const chooseSavedTemplate = async (template) => {
    if (template.id !== activeGuestTemplateId) {
      const activated = await activateSavedTemplate(template);
      if (!activated) return;
    }
    loadTemplateForEditing({ name: template.name, config: template.config }, { savedTemplateId: template.id });
    mobileSaveNoticeShownRef.current = true;
    setPreviewTemplateId(null);
    setPreviewSavedTemplateId(null);
    setMobileEditorExpanded(false);
    setMobileOpeningEditorOnly(false);
    setOpeningSettingsOpen(false);
    setMobileEditorOpen(true);
    setMobileEditorCategory('content');
    setWorkspaceView('editor');
  };

  const chooseLibraryTemplate = async (template) => {
    if (usingLibraryTemplateId) return;
    setUsingLibraryTemplateId(template.id);
    try {
      const response = await api.post(`/events/${currentEvent.id}/web-invite/saved`, {
        name: savedNamePlaceholder(template.name),
        config: completeInviteSections(template.config),
      });
      const created = response.data;
      setSavedTemplates((items) => [created, ...items]);
      setTemplateCollectionTab('mine');
      setActiveSavedTemplateId(created.id);
      setPreviewTemplateId(null);
      await api.post(`/events/${currentEvent.id}/web-invite/saved/${created.id}/activate`);
      setActiveGuestTemplateId(created.id);
      setSavedTemplates((items) => items.map((item) => ({ ...item, is_active: item.id === created.id })));
      loadTemplateForEditing({ name: created.name, config: created.config || template.config }, { savedTemplateId: created.id, announce: false });
      mobileSaveNoticeShownRef.current = false;
      setPreviewSavedTemplateId(null);
      setMobileEditorExpanded(false);
      setMobileOpeningEditorOnly(false);
      setOpeningSettingsOpen(false);
      setMobileEditorOpen(true);
      setMobileEditorCategory('content');
      setWorkspaceView('editor');
      toast.success(`${template.name} selected. Personalize it, then share when ready.`);
      return true;
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not use this template. Check My templates before trying again.'));
      return false;
    } finally {
      setUsingLibraryTemplateId(null);
    }
  };

  const toggleShareGuest = (guestId, checked) => {
    setSelectedShareGuestIds((current) => (
      checked
        ? [...new Set([...current, guestId])]
        : current.filter((id) => id !== guestId)
    ));
  };

  const filteredShareGuests = shareGuests.filter((guest) => {
    const query = shareGuestSearch.trim().toLowerCase();
    if (!query) return true;
    return [guest.name, guest.phone, guest.email]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(query));
  });

  const eligibleFilteredShareGuests = filteredShareGuests.filter((guest) => guest.phone);

  const toggleAllFilteredShareGuests = (checked) => {
    const visibleIds = eligibleFilteredShareGuests.map((guest) => guest.id).filter(Boolean);
    setSelectedShareGuestIds((current) => (
      checked
        ? [...new Set([...current, ...visibleIds])]
        : current.filter((id) => !visibleIds.includes(id))
    ));
  };

  const sendSavedTemplateToGuests = async () => {
    if (!shareTemplate || !selectedShareGuestIds.length || sendingShare) return;
    const recipientCount = selectedShareGuestIds.length;
    toast.info(
      recipientCount === 1
        ? 'Sending WhatsApp invitation to 1 recipient...'
        : `Sending WhatsApp invitation to ${recipientCount} recipients...`,
    );
    setSendingShare(true);
    try {
      const formData = new FormData();
      formData.append('send_to_all', 'false');
      formData.append('web_invite_template_id', shareTemplate.id);
      selectedShareGuestIds.forEach((guestId) => formData.append('guest_ids', guestId));
      const response = await api.post(
        `/events/${currentEvent.id}/whatsapp/send`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      const summary = response.data?.summary || {};
      toast.success(`Invitations sent: ${summary.sent || 0} sent, ${summary.failed || 0} failed, ${summary.skipped || 0} skipped`);
      setShareTemplate(null);
      setSelectedShareGuestIds([]);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not send the invitations.'));
    } finally {
      setSendingShare(false);
    }
  };

  const deleteSavedTemplate = async () => {
    if (!pendingDeleteTemplate || deletingSavedTemplate) return;
    const template = pendingDeleteTemplate;
    setDeletingSavedTemplate(true);
    try {
      await api.delete(`/events/${currentEvent.id}/web-invite/saved/${template.id}`);
      const remainingTemplates = savedTemplates.filter((item) => item.id !== template.id);
      setSavedTemplates(remainingTemplates);
      if (!remainingTemplates.length) setTemplateCollectionTab('library');
      if (activeSavedTemplateId === template.id) {
        setActiveSavedTemplateId(null);
        mobileSaveNoticeShownRef.current = false;
      }
      if (activeGuestTemplateId === template.id) setActiveGuestTemplateId(null);
      if (previewSavedTemplateId === template.id) {
        setPreviewSavedTemplateId(null);
        setWorkspaceView('templates');
      }
      if (shareTemplate?.id === template.id) setShareTemplate(null);
      setPendingDeleteTemplate(null);
      toast.success(`${template.name} removed from My templates`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not remove this invitation.'));
    } finally {
      setDeletingSavedTemplate(false);
    }
  };

  const selectedAIConcept = aiConcepts.find((item) => item.id === selectedAIConceptId) || null;
  const selectedRevision = revisions.find((item) => item.revision === selectedRevisionNumber) || null;

  const generateAIConcepts = async () => {
    const prompt = aiPrompt.trim();
    if (prompt.length < 3) {
      toast.error('Tell the creative studio a little about the feeling you want.');
      return;
    }
    setAiGenerating(true);
    setAiHeroAsset(null);
    const requestEventId = currentEvent.id;
    try {
      const response = await api.post(`/events/${requestEventId}/web-invite/ai/concepts`, {
        prompt,
        creativity: aiCreativity,
        preserve_current_palette: aiPreservePalette,
      });
      if (eventIdRef.current !== requestEventId) return;
      const concepts = response.data?.concepts || [];
      setAiConcepts(concepts);
      setSelectedAIConceptId(concepts[0]?.id || null);
      if (concepts.length) toast.success('Three editable directions are ready');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not generate creative directions.'));
    } finally {
      setAiGenerating(false);
    }
  };

  const applyAIConcept = () => {
    if (!selectedAIConcept) return;
    const suggestions = new Map(
      (selectedAIConcept.section_copy || []).map((item) => [item.section_type, item]),
    );
    setConfigAndRef((current) => ({
      ...current,
      theme: { ...current.theme, ...selectedAIConcept.theme },
      effects: { ...current.effects, ...selectedAIConcept.effects },
      sections: current.sections.map((section) => {
        const suggestion = suggestions.get(section.type);
        if (!suggestion) return section;
        const overrides = { ...section.overrides };
        if (suggestion.headline) overrides.headline = suggestion.headline;
        if (suggestion.body) overrides.body = suggestion.body;
        return { ...section, overrides };
      }),
    }));
    setWorkspaceView('editor');
    toast.success(`${selectedAIConcept.name} applied. Every setting is still editable.`);
  };

  const generateAIHero = async () => {
    if (!selectedAIConcept) return;
    setAiHeroGenerating(true);
    setAiHeroAsset(null);
    const requestEventId = currentEvent.id;
    try {
      const response = await api.post(`/events/${requestEventId}/web-invite/ai/hero-image`, {
        prompt: selectedAIConcept.hero_image_prompt,
        style: aiHeroStyle,
      });
      if (eventIdRef.current !== requestEventId) return;
      setAiHeroAsset(response.data);
      toast.success('Hero artwork is ready to review');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not generate hero artwork.'));
    } finally {
      setAiHeroGenerating(false);
    }
  };

  const useAIHero = () => {
    if (!aiHeroAsset?.url) return;
    const hero = configRef.current?.sections?.find((section) => section.type === 'hero');
    if (!hero) {
      toast.error('Add a hero section before using this artwork.');
      return;
    }
    updateSection(hero.id, (section) => ({
      ...section,
      source_mode: section.source_mode === 'custom' ? 'mixed' : section.source_mode,
      overrides: { ...section.overrides, image_url: aiHeroAsset.url },
    }));
    setSelectedSectionId(hero.id);
    setWorkspaceView('editor');
    setAiStudioOpen(false);
    toast.success('Hero artwork applied to the invite');
  };

  const flushDraft = async () => {
    let response = null;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      response = await persistDraft();
      if (snapshotKey(configRef.current, slugRef.current) === baselineRef.current) break;
    }
    return response;
  };

  const persistEditorToMyTemplates = ({
    targetTemplate = null,
    forceCreate = false,
    templateName = '',
    skipDuplicateCheck = false,
    announceDraft = true,
  } = {}) => {
    if (!mobileEditorDirtyRef.current) return Promise.resolve(null);
    if (mobileTemplateSaveInFlightRef.current) return mobileTemplateSaveInFlightRef.current;

    if (!skipDuplicateCheck && !activeSavedTemplateId) {
      const sourceTemplateId = configRef.current?.template_id;
      const libraryTemplate = WEB_INVITE_TEMPLATES.find((item) => item.id === sourceTemplateId);
      const matchingTemplates = savedTemplates.filter((item) => (
        (item.source_template_id || item.config?.template_id) === sourceTemplateId
      ));
      const existingTemplate = matchingTemplates.find((item) => item.name === libraryTemplate?.name)
        || matchingTemplates[0];
      if (libraryTemplate && existingTemplate) {
        const copyName = nextTemplateCopyName(libraryTemplate.name, savedTemplates);
        setDuplicateTemplatePrompt({ existingTemplate, libraryTemplate });
        setDuplicateSaveMode('copy');
        setDuplicateTemplateNames({ replace: existingTemplate.name, copy: copyName });
        return Promise.resolve(null);
      }
    }

    const request = (async () => {
      let savedTemplate = forceCreate
        ? null
        : targetTemplate || savedTemplates.find((item) => item.id === activeSavedTemplateId) || null;
      setSavingNamedTemplate(true);
      try {
        while (mobileEditorDirtyRef.current) {
          const templateConfig = cloneInviteConfig(configRef.current);
          const configKey = JSON.stringify(templateConfig);
          await flushDraft();

          if (savedTemplate) {
            const response = await api.put(
              `/events/${currentEvent.id}/web-invite/saved/${savedTemplate.id}`,
              {
                name: templateName || savedTemplate.name,
                config: templateConfig,
                expected_revision: savedTemplate.revision,
              },
            );
            savedTemplate = {
              ...response.data,
              is_active: response.data.id === activeGuestTemplateId,
            };
          } else {
            const baseTemplateName = WEB_INVITE_TEMPLATES.find(
              (item) => item.id === templateConfig?.template_id,
            )?.name || 'My invitation';
            const response = await api.post(`/events/${currentEvent.id}/web-invite/saved`, {
              name: savedNamePlaceholder(templateName || baseTemplateName),
              config: templateConfig,
            });
            savedTemplate = response.data;
            setTemplateCollectionTab('mine');
          }

          const persistedTemplate = savedTemplate;
          setActiveSavedTemplateId(persistedTemplate.id);
          setSavedTemplates((current) => [
            persistedTemplate,
            ...current.filter((item) => item.id !== persistedTemplate.id),
          ]);
          if (JSON.stringify(configRef.current) === configKey) mobileEditorDirtyRef.current = false;
          if (announceDraft && !mobileSaveNoticeShownRef.current) {
            toast.success('Draft saved to My templates');
            mobileSaveNoticeShownRef.current = true;
          }
        }
        return savedTemplate;
      } catch (error) {
        toast.error(getApiErrorMessage(error, 'Could not save this invitation to My templates.'));
        return null;
      } finally {
        setSavingNamedTemplate(false);
      }
    })();

    const trackedRequest = request.finally(() => {
      mobileTemplateSaveInFlightRef.current = null;
    });
    mobileTemplateSaveInFlightRef.current = trackedRequest;
    return trackedRequest;
  };

  const resolveDuplicateTemplate = async () => {
    if (!duplicateTemplatePrompt || resolvingDuplicateTemplate) return;
    const fallbackName = duplicateSaveMode === 'replace'
      ? duplicateTemplatePrompt.existingTemplate.name
      : nextTemplateCopyName(duplicateTemplatePrompt.libraryTemplate.name, savedTemplates);
    const resolvedName = (duplicateTemplateNames[duplicateSaveMode]?.trim() || fallbackName).slice(0, 80);
    setResolvingDuplicateTemplate(true);
    try {
      const savedTemplate = await persistEditorToMyTemplates({
        targetTemplate: duplicateSaveMode === 'replace'
          ? duplicateTemplatePrompt.existingTemplate
          : null,
        forceCreate: duplicateSaveMode === 'copy',
        templateName: resolvedName,
        skipDuplicateCheck: true,
        announceDraft: false,
      });
      if (!savedTemplate) return;
      mobileSaveNoticeShownRef.current = true;
      setTemplateCollectionTab('mine');
      setDuplicateTemplatePrompt(null);
      toast.success(
        duplicateSaveMode === 'replace'
          ? `${resolvedName} replaced with your changes`
          : `${resolvedName} saved to My templates`,
      );
    } finally {
      setResolvingDuplicateTemplate(false);
    }
  };

  const closeMobileEditor = () => {
    setMobileEditorOpen(false);
    setMobileEditorExpanded(false);
    persistEditorToMyTemplates().catch(() => {});
  };

  const returnToTemplates = () => {
    closeMobileEditor();
    setWorkspaceView('templates');
  };

  const fetchRevisionHistory = async () => {
    const requestEventId = currentEvent.id;
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const response = await api.get(`/events/${requestEventId}/web-invite/revisions`, {
        params: { limit: 50 },
      });
      if (eventIdRef.current !== requestEventId) return;
      const nextRevisions = response.data?.revisions || [];
      setRevisions(nextRevisions);
      setSelectedRevisionNumber((current) => (
        nextRevisions.some((item) => item.revision === current)
          ? current
          : nextRevisions.find((item) => item.revision === inviteRef.current?.revision)?.revision
            || nextRevisions[0]?.revision
            || null
      ));
    } catch (error) {
      if (eventIdRef.current !== requestEventId) return;
      setHistoryError(getApiErrorMessage(error, 'Could not load invite history.'));
    } finally {
      if (eventIdRef.current === requestEventId) setHistoryLoading(false);
    }
  };

  const openRevisionHistory = async () => {
    if (!isValidSlug(slugRef.current)) {
      toast.error('Choose a valid invite address before opening version history.');
      return;
    }
    if (saveStateRef.current === 'conflict') {
      toast.error('Reload the invite before opening version history.');
      return;
    }
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryError('');
    try {
      await flushDraft();
      await fetchRevisionHistory();
    } catch (error) {
      setHistoryLoading(false);
      setHistoryError(getApiErrorMessage(error, 'Save the current draft before opening history.'));
    }
  };

  const restoreRevision = async () => {
    if (!pendingRestoreRevision || restoringRevision) return;
    setRestoringRevision(true);
    try {
      const response = await api.post(
        `/events/${currentEvent.id}/web-invite/revisions/${pendingRestoreRevision.revision}/restore`,
        { expected_revision: inviteRef.current.revision },
      );
      applyManagementResponse(response.data, { establishBaseline: true });
      setSelectedSectionId('hero');
      setActiveSavedTemplateId(null);
      mobileSaveNoticeShownRef.current = false;
      setPendingRestoreRevision(null);
      setHistoryOpen(false);
      toast.success(`Version ${pendingRestoreRevision.revision} restored as a new draft version`);
    } catch (error) {
      if (error.response?.status === 409) {
        updateSaveState('conflict');
        setHistoryError('The invite changed in another session. Reload before restoring a version.');
        setPendingRestoreRevision(null);
        setHistoryOpen(true);
      }
      toast.error(getApiErrorMessage(error, 'Could not restore this invite version.'));
    } finally {
      setRestoringRevision(false);
    }
  };

  if (!currentEvent) return <EmptyState />;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="grid min-h-[60vh] place-items-center"><div className="h-10 w-10 animate-spin rounded-full border-4 border-primary/20 border-t-primary" /></div>
      </DashboardLayout>
    );
  }

  if (loadError || !config) {
    return (
      <DashboardLayout>
        <div className="mx-auto max-w-xl py-16 text-center">
          <Warning className="mx-auto h-10 w-10 text-destructive" />
          <h1 className="mt-4 text-2xl font-semibold">Could not open the builder</h1>
          <p className="mt-2 text-sm text-muted-foreground">{loadError}</p>
        </div>
      </DashboardLayout>
    );
  }

  const selectedDefinition = sectionDefinition(selectedSection?.type);
  const addableSections = sortedSections.filter((section) => !section.enabled && section.type !== 'custom');
  const activeEditingTemplate = savedTemplates.find((item) => item.id === activeSavedTemplateId && item.id === activeGuestTemplateId) || null;
  const saveStatusLabel = !hasEditorChanges
    ? ''
    : saveState === 'saving'
      ? 'Saving...'
      : saveState === 'pending'
        ? 'Saving soon...'
        : saveState === 'saved'
          ? 'Saved'
          : saveState === 'error'
            ? 'Save failed'
            : saveState === 'conflict'
              ? 'Reload required'
              : '';
  const mobileMusicControlVisible = !mobilePreviewShowingOpening
    && config.music?.enabled
    && (config.music.track_id !== 'custom' || Boolean(config.music.custom_audio_url));
  const mobileFloatingEditPosition = mobileMusicControlVisible
    ? 'bottom-[calc(1.625rem+env(safe-area-inset-bottom))] right-24'
    : 'bottom-[calc(1.25rem+env(safe-area-inset-bottom))] right-4';
  const previewTemplate = libraryTemplates.find((item) => item.id === previewTemplateId) || null;
  const previewSavedTemplate = savedTemplates.find((item) => item.id === previewSavedTemplateId) || null;
  const previewConfig = previewSavedTemplate?.config || (previewTemplate
    ? completeInviteSections(previewTemplate.config)
    : config);
  const previewingCurrentDraft = !previewTemplate && !previewSavedTemplate;
  const inlinePreviewTemplate = templateCollectionTab === 'library'
    ? (previewTemplate || libraryTemplates.find((item) => item.id === DEFAULT_WEB_INVITE_TEMPLATE_ID) || libraryTemplates[0] || null)
    : null;
  const inlinePreviewSavedTemplate = templateCollectionTab === 'mine'
    ? (previewSavedTemplate || savedTemplates[0] || null)
    : null;
  const inlinePreviewConfig = inlinePreviewSavedTemplate?.config || (inlinePreviewTemplate
    ? completeInviteSections(inlinePreviewTemplate.config)
    : null);

  const openInlinePreviewFullScreen = () => {
    if (inlinePreviewTemplate) {
      setPreviewTemplateId(inlinePreviewTemplate.id);
      setPreviewSavedTemplateId(null);
    } else if (inlinePreviewSavedTemplate) {
      setPreviewTemplateId(null);
      setPreviewSavedTemplateId(inlinePreviewSavedTemplate.id);
    }
    setWorkspaceView('preview');
  };

  const customizeInlinePreview = async () => {
    if (inlinePreviewTemplate) {
      await chooseLibraryTemplate(inlinePreviewTemplate);
      return;
    }
    if (inlinePreviewSavedTemplate) {
      if (inlinePreviewSavedTemplate.id !== activeGuestTemplateId) {
        const activated = await activateSavedTemplate(inlinePreviewSavedTemplate);
        if (!activated) return;
      }
      loadTemplateForEditing(
        { name: inlinePreviewSavedTemplate.name, config: inlinePreviewSavedTemplate.config },
        { savedTemplateId: inlinePreviewSavedTemplate.id },
      );
      mobileSaveNoticeShownRef.current = true;
    }
    setPreviewTemplateId(null);
    setPreviewSavedTemplateId(null);
    setMobileEditorExpanded(false);
    setMobileOpeningEditorOnly(false);
    setOpeningSettingsOpen(false);
    setMobileEditorOpen(true);
    setMobileEditorCategory('content');
    setWorkspaceView('editor');
  };

  const editorActionsMenu = (triggerClassName = '') => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size="icon" className={triggerClassName} aria-label="More invitation actions">
          <DotsThreeVertical className="h-5 w-5" weight="bold" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="z-[130] w-52">
        {/* <DropdownMenuItem onSelect={saveCurrentNamedTemplate} disabled={savingNamedTemplate || saveState === 'saving' || saveState === 'conflict'}>
          <FloppyDisk className="mr-2 h-4 w-4" />Save named version
        </DropdownMenuItem> */}
        <DropdownMenuItem onSelect={saveCurrentAsTemplate} disabled={savingNamedTemplate || saveState === 'saving' || saveState === 'conflict'}>
          <Copy className="mr-2 h-4 w-4" />Save a copy
        </DropdownMenuItem>
        {/* <DropdownMenuItem onSelect={openRevisionHistory} disabled={saveState === 'saving' || saveState === 'conflict'}>
          <ClockCounterClockwise className="mr-2 h-4 w-4" />Version history
        </DropdownMenuItem> */}
      </DropdownMenuContent>
    </DropdownMenu>
  );
  return (
    <DashboardLayout
      immersive={workspaceView === 'preview' || workspaceView === 'editor'}
      hideChat={workspaceView === 'preview'}
      replaceMobileChat={workspaceView === 'editor'}
      hideFooter={workspaceView === 'preview' || workspaceView === 'editor'}
    >
      {workspaceView === 'templates' && (
        <div className="mx-auto max-w-7xl space-y-8 pb-12">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-primary/10 text-primary"><Sparkle className="h-6 w-6" weight="fill" /></div>
            <h1 className="mt-5 font-serif text-3xl font-bold text-foreground sm:text-5xl">Choose your invitation</h1>
          </div>

          {/* AI Creative Studio is temporarily hidden while the full-template generation flow is being developed.
          <Card className="border-violet-200 bg-gradient-to-br from-violet-50 via-background to-amber-50">
            <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-start gap-3">
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-violet-100 text-violet-700"><Sparkle className="h-5 w-5" weight="fill" /></div>
                <div><h2 className="font-serif text-lg font-semibold">AI Creative Studio</h2><p className="mt-1 text-sm text-muted-foreground">Explore personal directions and hero artwork, then apply them to your invite.</p></div>
              </div>
              <AIPremiumButton featureName="Web Invite Creative Studio" className="shrink-0" onClick={() => setAiStudioOpen(true)}><MagicWand className="h-4 w-4" />Open Creative Studio</AIPremiumButton>
            </CardContent>
          </Card>
          */}

          <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_minmax(460px,1fr)] xl:items-start">
            <div className="min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h2 className="min-w-0 font-serif text-xl font-semibold sm:text-2xl">{templateCollectionTab === 'mine' ? 'My templates' : 'Template library'}</h2>
              <div className="ml-auto flex shrink-0 rounded-lg border bg-muted p-1" role="tablist" aria-label="Invitation collections">
                <Button type="button" role="tab" aria-selected={templateCollectionTab === 'mine'} size="sm" className="h-8 px-2 text-xs sm:px-3 sm:text-sm" variant={templateCollectionTab === 'mine' ? 'secondary' : 'ghost'} onClick={() => setTemplateCollectionTab('mine')}><span className="sm:hidden">Mine ({savedTemplates.length})</span><span className="hidden sm:inline">My templates ({savedTemplates.length})</span></Button>
                <Button type="button" role="tab" aria-selected={templateCollectionTab === 'library'} size="sm" className="h-8 px-2 text-xs sm:px-3 sm:text-sm" variant={templateCollectionTab === 'library' ? 'secondary' : 'ghost'} onClick={() => setTemplateCollectionTab('library')}><span className="sm:hidden">Library</span><span className="hidden sm:inline">Template library</span></Button>
              </div>
            </div>

            {savedTemplatesLoading && <div className="mt-8 grid min-h-52 place-items-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary/20 border-t-primary" /></div>}

            {!savedTemplatesLoading && templateCollectionTab === 'mine' && (
                <div className="mt-5 grid grid-cols-2 items-start gap-3 sm:gap-5">
                  {savedTemplates.map((template) => {
                  const theme = template.config?.theme || {};
                  const preview = {
                    swatches: [theme.primary_color, theme.secondary_color, theme.background_color].filter(Boolean),
                    config: template.config,
                  };
                  return (
                    <Card key={template.id} role="button" tabIndex={0} onClick={() => openSavedTemplatePreview(template)} onKeyDown={(event) => { if (event.target === event.currentTarget && ['Enter', ' '].includes(event.key)) openSavedTemplatePreview(template); }} className={`group cursor-pointer overflow-hidden transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-lg ${inlinePreviewSavedTemplate?.id === template.id ? 'border-primary ring-2 ring-primary/15' : ''}`}>
                      <CardContent className="p-2 sm:p-3">
                        <TemplatePreview template={preview} />
                        <div className="px-1 pb-1 pt-4">
                          <div className="flex items-start justify-between gap-2"><div className="min-w-0"><p className="truncate text-sm font-semibold sm:text-base">{template.name}</p><p className="mt-1 text-[0.58rem] uppercase tracking-[0.12em] text-muted-foreground sm:text-[0.65rem] sm:tracking-[0.16em]">Saved invite</p></div>{activeSavedTemplateId === template.id && <Badge variant="outline" className="hidden sm:inline-flex">Editing</Badge>}</div>
                          <p className="mt-3 hidden text-xs leading-5 text-muted-foreground sm:block">Updated {readableRevisionDate(template.updated_at)}</p>
                          <div className="mt-4 grid grid-cols-2 gap-2" onClick={(event) => event.stopPropagation()}>
                            {activeGuestTemplateId === template.id ? <>
                              <Button type="button" size="sm" variant="outline" onClick={() => copySavedTemplateLink(template)}><Copy className="mr-1.5 h-4 w-4" />Copy link</Button>
                              <Button type="button" size="sm" onClick={() => shareSavedTemplate(template)}><ShareNetwork className="mr-1.5 h-4 w-4" />Share</Button>
                            </> : <Button type="button" size="sm" className="col-span-2" onClick={() => chooseSavedTemplate(template)}>Make active for guests</Button>}
                            <Button type="button" size="sm" variant="ghost" className="col-span-2 text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setPendingDeleteTemplate(template)}><Trash className="mr-1.5 h-4 w-4" />Remove from My templates</Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                  })}
                  {savedTemplates.length === 0 ? (
                    <SavedTemplatePlaceholder bare onBrowse={() => setTemplateCollectionTab('library')} />
                  ) : savedTemplates.length < 3 ? (
                    <SavedTemplatePlaceholder onBrowse={() => setTemplateCollectionTab('library')} />
                  ) : null}
                </div>
            )}

            {!savedTemplatesLoading && templateCollectionTab === 'library' && (
              <div className="mt-5 grid grid-cols-2 items-start gap-3 sm:gap-5">
                {libraryTemplates.map((template) => (
                  <Card key={template.id} role="button" tabIndex={0} onClick={() => openTemplatePreview(template)} onKeyDown={(event) => { if (event.target === event.currentTarget && ['Enter', ' '].includes(event.key)) openTemplatePreview(template); }} className={`group cursor-pointer overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-lg ${inlinePreviewTemplate?.id === template.id ? 'border-primary ring-2 ring-primary/15' : ''}`}>
                    <CardContent className="p-2 sm:p-3">
                      <TemplatePreview template={template} />
                      <div className="px-1 pb-1 pt-4">
                        <div><p className="text-sm font-semibold leading-tight sm:text-base">{template.name}</p><p className="mt-1 truncate text-[0.58rem] uppercase tracking-[0.1em] text-muted-foreground sm:text-[0.65rem] sm:tracking-[0.16em]">{template.mood}</p></div>
                        <p className="mt-3 hidden min-h-10 text-xs leading-5 text-muted-foreground sm:block">{template.description}</p>
                        <Button type="button" size="sm" variant="outline" className="mt-3 w-full px-2 text-xs group-hover:border-primary/40 sm:mt-4 sm:text-sm" disabled={Boolean(usingLibraryTemplateId)} onClick={(event) => { event.stopPropagation(); chooseLibraryTemplate(template); }}><BookmarkSimple className="mr-1 h-3.5 w-3.5 sm:mr-2 sm:h-4 sm:w-4" />{usingLibraryTemplateId === template.id ? 'Making active...' : 'Make active for guests'}</Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {libraryTemplates.length === 0 && (
                  <Card className="col-span-2 border-dashed bg-muted/20">
                    <CardContent className="py-12 text-center">
                      <p className="font-serif text-xl font-semibold">Birthday templates are coming soon.</p>
                      <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">You can still customize and publish this birthday web invite from the editor.</p>
                      <Button type="button" className="mt-5" onClick={() => setWorkspaceView('editor')}>Customize invite</Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
            </div>

            <div className="sticky top-4 hidden min-w-0 xl:block">
              {inlinePreviewConfig ? (
                <Card className="overflow-hidden shadow-lg">
                  <div className="border-b bg-muted/30 px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-serif text-xl font-semibold">{inlinePreviewSavedTemplate?.name || inlinePreviewTemplate?.name}</p>
                        <p className="mt-1 text-xs text-muted-foreground">Live mobile preview using {currentEvent.name || 'your event'} details</p>
                      </div>
                      <Badge variant="outline">Mobile</Badge>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {inlinePreviewSavedTemplate && activeGuestTemplateId === inlinePreviewSavedTemplate.id && <Button type="button" size="sm" variant="outline" onClick={() => copySavedTemplateLink(inlinePreviewSavedTemplate)}><Copy className="mr-1.5 h-4 w-4" />Copy link</Button>}
                      <Button type="button" size="sm" disabled={Boolean(inlinePreviewTemplate && usingLibraryTemplateId)} onClick={customizeInlinePreview}><Check className="mr-1.5 h-4 w-4" />{inlinePreviewTemplate && usingLibraryTemplateId === inlinePreviewTemplate.id ? 'Making active...' : inlinePreviewSavedTemplate?.id === activeGuestTemplateId ? 'Edit invitation' : 'Make active for guests'}</Button>
                      <Button type="button" size="sm" variant="ghost" onClick={openInlinePreviewFullScreen}>Open full preview</Button>
                    </div>
                  </div>
                  <CardContent className="bg-slate-100 p-4">
                    <div className="mx-auto max-h-[calc(100vh-15rem)] max-w-[430px] overflow-y-auto rounded-[1.75rem] bg-white shadow-xl">
                      <WebInviteRenderer config={inlinePreviewConfig} eventData={currentEvent} preview onAction={() => toast.info('Guest actions become available from the saved invitation link.')} />
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-dashed"><CardContent className="py-16 text-center text-sm text-muted-foreground">Choose a template to preview it here.</CardContent></Card>
              )}
            </div>
          </div>
        </div>
      )}

      {workspaceView === 'preview' && (
        <div className="fixed inset-0 z-[80] bg-background xl:static xl:mx-auto xl:max-w-7xl xl:space-y-5 xl:bg-transparent xl:pb-12">
          <div className="absolute left-3 top-[calc(0.75rem+env(safe-area-inset-top))] z-20 xl:static xl:flex xl:flex-row xl:items-center xl:justify-between xl:gap-4">
            <div className="flex items-start gap-3"><Button type="button" variant="outline" size="icon" className="bg-background/90 shadow-lg backdrop-blur xl:bg-background xl:shadow-none" onClick={() => setWorkspaceView(previewingCurrentDraft ? 'editor' : 'templates')} aria-label={previewingCurrentDraft ? 'Back to customization' : 'Back to templates'}><ArrowLeft className="h-4 w-4" /></Button><div className="hidden xl:block"><div className="flex flex-wrap items-center gap-2"><h1 className="font-serif text-3xl font-bold">{previewSavedTemplate?.name || previewTemplate?.name || 'Invitation preview'}</h1>{previewTemplate && <Badge variant="outline">Template library</Badge>}{previewSavedTemplate && <Badge variant="outline">My template</Badge>}</div></div></div>
            <div className="hidden flex-wrap gap-2 xl:flex">
              {previewSavedTemplate && activeGuestTemplateId === previewSavedTemplate.id && <Button type="button" variant="outline" onClick={() => copySavedTemplateLink(previewSavedTemplate)}><Copy className="mr-2 h-4 w-4" />Copy link</Button>}
              {previewSavedTemplate && <Button type="button" variant="ghost" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setPendingDeleteTemplate(previewSavedTemplate)}><Trash className="mr-2 h-4 w-4" />Remove</Button>}
              <Button type="button" disabled={Boolean(previewTemplate && usingLibraryTemplateId)} onClick={() => { if (previewTemplate || previewSavedTemplate) makePreviewActiveForGuests(); else setWorkspaceView('editor'); }}><Check className="mr-2 h-4 w-4" />{previewTemplate && usingLibraryTemplateId === previewTemplate.id ? 'Making active...' : previewSavedTemplate?.id === activeGuestTemplateId ? 'Edit invitation' : previewTemplate || previewSavedTemplate ? 'Make active for guests' : 'Edit invitation'}</Button>
            </div>
          </div>

          <Card className="h-full overflow-hidden rounded-none border-0 shadow-none xl:h-auto xl:rounded-xl xl:border xl:shadow-sm">
            <CardContent className="h-full bg-white p-0 xl:bg-slate-100 xl:p-5">
              <div className="mx-auto h-full overflow-y-auto bg-white xl:max-h-[calc(100vh-12rem)] xl:max-w-[430px] xl:rounded-[1.75rem] xl:shadow-xl">
                <WebInviteRenderer
                  config={previewConfig}
                  eventData={currentEvent}
                  preview
                  onVisibleSectionChange={handlePreviewVisibleSection}
                  onOpeningStateChange={setMobilePreviewShowingOpening}
                  onAction={() => toast.info('Guest actions become available from the saved invitation link.')}
                />
              </div>
            </CardContent>
          </Card>
          <Button
            type="button"
            size="icon"
            disabled={Boolean(previewTemplate && usingLibraryTemplateId)}
            className={`fixed z-[90] h-14 w-14 rounded-full shadow-2xl transition-[bottom,right,opacity,transform] duration-300 ease-out animate-in fade-in-0 zoom-in-95 xl:hidden ${mobileFloatingEditPosition}`}
            onClick={() => {
              if (previewTemplate || previewSavedTemplate) editPreviewedTemplate();
              else {
                if (previewConfig?.opening?.enabled !== false && mobilePreviewShowingOpening) openMobileOpeningEditor();
                else {
                  setEditorPreviewInitiallyOpened(true);
                  openMobileEditor('content', selectedSectionId);
                  previewScrollRequestRef.current += 1;
                  setPreviewScrollTarget({ id: selectedSectionId, request: previewScrollRequestRef.current });
                }
                setWorkspaceView('editor');
              }
            }}
            aria-label={previewTemplate || previewSavedTemplate ? 'Edit this design' : 'Edit invitation'}
          >
            <PencilSimple className="h-6 w-6" weight="bold" />
          </Button>
        </div>
      )}

      {workspaceView === 'editor' && (
      <div className="xl:space-y-5 xl:pb-10">
        <div className="fixed inset-0 z-[80] bg-white xl:hidden">
          <div className="h-full overflow-y-auto overscroll-contain bg-white">
            <WebInviteRenderer
              config={config}
              eventData={currentEvent}
              preview
              onVisibleSectionChange={handlePreviewVisibleSection}
              onEditSection={(sectionId) => openMobileEditor('content', sectionId)}
              onOpeningStateChange={setMobilePreviewShowingOpening}
              scrollToSection={previewScrollTarget}
              initiallyOpened={editorPreviewInitiallyOpened}
              onAction={() => toast.info('Guests will be asked to sign in only when they use this action.')}
            />
          </div>
          {!mobileEditorOpen && (
            <Button type="button" size="lg" className={`fixed z-[90] rounded-full px-5 shadow-2xl transition-[bottom,right,opacity,transform] duration-300 ease-out animate-in fade-in-0 zoom-in-95 xl:hidden ${mobileFloatingEditPosition}`} onClick={() => { if (mobilePreviewShowingOpening) openMobileOpeningEditor(); else openMobileEditor('content'); }}><MagicWand className="mr-2 h-5 w-5" />Edit invitation</Button>
          )}
        </div>

        <button
          type="button"
          className={`fixed inset-0 z-[90] bg-black/20 transition-opacity duration-300 xl:hidden ${mobileEditorOpen ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
          onClick={closeMobileEditor}
          aria-label="Close invitation editor"
        />

        <div className="pointer-events-none fixed inset-x-0 top-0 z-[110] flex items-start justify-between gap-3 p-3 pt-[calc(0.75rem+env(safe-area-inset-top))] xl:hidden">
          <Button type="button" variant="outline" size="icon" className="pointer-events-auto shrink-0 rounded-full bg-background/90 shadow-lg backdrop-blur" onClick={returnToTemplates} aria-label="Back to designs"><ArrowLeft className="h-4 w-4" /></Button>
          <div className="pointer-events-auto flex items-center gap-2">
            {editorActionsMenu('rounded-full bg-background/90 shadow-lg backdrop-blur')}
          </div>
        </div>

        <div className="hidden flex-col gap-4 xl:flex xl:flex-row xl:items-center xl:justify-between">
          <div>
            <Button type="button" variant="ghost" size="sm" className="mb-2 -ml-2 hidden xl:inline-flex" onClick={openCurrentPreview}><ImageSquare className="mr-2 h-4 w-4" />Open full preview</Button>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-serif text-3xl font-bold text-foreground sm:text-4xl">Personalize your invitation</h1>
              {activeSavedTemplateId && <Badge variant="outline">{savedTemplates.find((item) => item.id === activeSavedTemplateId)?.name || 'My template'}</Badge>}
            </div>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">Edit the content and style while the live preview stays in sync.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {hasEditorChanges && <span className="mr-1 text-xs text-muted-foreground" aria-live="polite">
              {saveState === 'saving' ? 'Saving…' : saveState === 'pending' ? 'Unsaved changes' : saveState === 'saved' ? 'Saved' : saveState === 'error' ? 'Save failed' : saveState === 'conflict' ? 'Reload required' : ''}
            </span>}
            {editorActionsMenu()}
          </div>
        </div>

        <div className="grid gap-5 xl:grid-cols-[minmax(360px,0.82fr)_minmax(520px,1.18fr)]">
          <div className={`fixed inset-x-0 bottom-0 z-[100] flex flex-col overflow-hidden rounded-t-[1.75rem] border-t bg-background shadow-[0_-24px_70px_rgba(15,23,42,0.22)] transition-[transform,max-height] duration-300 ease-out xl:sticky xl:top-4 xl:z-auto xl:max-h-[calc(100vh-7rem)] xl:self-start xl:translate-y-0 xl:overflow-visible xl:rounded-none xl:border-0 xl:bg-transparent xl:shadow-none ${mobileEditorExpanded ? 'max-h-[76dvh]' : 'max-h-[50dvh]'} ${mobileEditorOpen ? 'translate-y-0' : 'translate-y-full'}`}>
            <div className="shrink-0 border-b bg-background px-4 pb-3 pt-2 xl:hidden">
              <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-muted-foreground/25" aria-hidden="true" />
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-serif text-xl font-semibold">{mobileOpeningEditorOnly ? 'Opening & music' : 'Edit invitation'}</p>
                  {saveStatusLabel && <p className="text-xs text-muted-foreground" aria-live="polite">{saveStatusLabel}</p>}
                </div>
                <Button type="button" onClick={closeMobileEditor} className="rounded-full px-5">Done</Button>
              </div>
              {!mobileOpeningEditorOnly && <div className="mt-3 grid grid-cols-2 gap-1 rounded-xl bg-muted p-1" role="tablist" aria-label="Invitation editor sections">
                {['content', 'style'].map((category) => (
                  <button
                    key={category}
                    type="button"
                    role="tab"
                    aria-selected={mobileEditorCategory === category}
                    onClick={() => openMobileEditor(category)}
                    className={`rounded-lg px-2 py-2 text-sm font-medium capitalize transition ${mobileEditorCategory === category ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`}
                  >
                    {category}
                  </button>
                ))}
              </div>}
            </div>
            <div ref={editorOptionsScrollRef} onScroll={handleMobileEditorScroll} className="flex flex-col gap-5 overflow-y-auto overscroll-contain bg-muted/25 px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4 xl:max-h-[calc(100vh-7rem)] xl:bg-transparent xl:px-0 xl:pb-0 xl:pt-0 xl:pr-2">
            {mobileEditorCategory === 'content' && !mobileOpeningEditorOnly && (
              <div className="flex min-h-10 shrink-0 gap-2 overflow-x-auto pb-1 [scrollbar-color:hsl(var(--border))_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar]:h-[3px] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-track]:bg-transparent xl:hidden" aria-label="Invitation sections">
                {visibleSections.map((section) => (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => setSelectedSectionId(section.id)}
                    className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-medium transition ${selectedSection?.id === section.id ? 'border-primary bg-primary text-primary-foreground' : 'bg-background text-muted-foreground'}`}
                  >
                    {sectionDefinition(section.type).label}
                  </button>
                ))}
              </div>
            )}
            <div className={`order-3 shrink-0 xl:order-none ${mobileEditorCategory === 'content' ? '' : 'hidden xl:block'}`}>
            <Card className="overflow-hidden rounded-2xl border-border/80 shadow-sm">
              <Accordion
                type="single"
                collapsible
                value={mobileOpeningEditorOnly || openingSettingsOpen ? 'opening' : ''}
                onValueChange={(value) => { if (!mobileOpeningEditorOnly) setOpeningSettingsOpen(value === 'opening'); }}
              >
                <AccordionItem value="opening" className="border-0">
                  <AccordionTrigger className="bg-muted/35 px-5 py-4 hover:no-underline">
                    <span className="pr-3 text-left">
                      <span className="block font-serif text-lg font-semibold">Opening moment &amp; music</span>
                      <span className="mt-1 block text-xs font-normal leading-5 text-muted-foreground">Welcome guests and choose the soundtrack.</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4 px-5 pb-5 pt-4">
                <div className="flex items-center justify-between gap-3 rounded-xl border bg-background p-3">
                  <div><p className="text-sm font-medium">Opening screen</p><p className="text-xs text-muted-foreground">Guests tap once to enter the invitation.</p></div>
                  <Switch checked={config.opening.enabled} onCheckedChange={(checked) => updateConfig('opening.enabled', checked)} aria-label="Show invitation opening screen" />
                </div>
                {config.opening.enabled && (
                  <>
                    <div className="space-y-1.5"><Label>Opening line</Label><Input value={config.opening.eyebrow} maxLength={80} onChange={(event) => updateConfig('opening.eyebrow', event.target.value)} placeholder="A celebration awaits" /></div>
                    <div className="space-y-1.5"><Label>Opening title</Label><Input value={config.opening.title} maxLength={120} onChange={(event) => updateConfig('opening.title', event.target.value)} placeholder="Something beautiful begins" /></div>
                    <div className="space-y-1.5"><Label>Welcome message</Label><Textarea value={config.opening.message} maxLength={280} rows={3} onChange={(event) => updateConfig('opening.message', event.target.value)} /></div>
                    <div className="space-y-1.5"><Label>Entry button</Label><Input value={config.opening.action_label} maxLength={50} onChange={(event) => updateConfig('opening.action_label', event.target.value)} placeholder="Unfold the invitation" /></div>
                  </>
                )}

                <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
                  <div><p className="text-sm font-medium">Invitation music</p><p className="text-xs text-muted-foreground">Starts only after the guest opens the invitation.</p></div>
                  <Switch checked={config.music.enabled} onCheckedChange={(checked) => updateConfig('music.enabled', checked)} aria-label="Play invitation music" />
                </div>

                {config.music.enabled && (
                  <>
                    <div className="space-y-2">
                      <Label>Soundtrack</Label>
                      <div className="grid gap-2">
                        {WEB_INVITE_MUSIC_TRACKS.map((track) => (
                          <button key={track.id} type="button" onClick={() => updateConfig('music.track_id', track.id)} className={`flex items-center gap-3 rounded-lg border p-3 text-left transition-colors ${config.music.track_id === track.id ? 'border-primary bg-primary/5' : 'hover:bg-muted/40'}`}>
                            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/10 text-primary"><MusicNotes className="h-4 w-4" weight="duotone" /></span>
                            <span className="min-w-0 flex-1"><span className="block text-sm font-medium">{track.name}</span><span className="block text-xs text-muted-foreground">{track.id === 'custom' && config.music.custom_audio_name ? config.music.custom_audio_name : track.description}</span></span>
                            {config.music.track_id === track.id && <Check className="h-4 w-4 text-primary" weight="bold" />}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2"><div className="flex justify-between text-xs"><Label>Music volume</Label><span className="text-muted-foreground">{Math.round(config.music.volume * 100)}%</span></div><Slider value={[config.music.volume]} min={0.05} max={0.7} step={0.05} onValueChange={([value]) => updateConfig('music.volume', value)} /></div>

                    <label className={`flex cursor-pointer items-center justify-center rounded-lg border border-dashed px-4 py-3 text-sm font-medium transition-colors hover:bg-muted/35 ${uploadingMusic ? 'pointer-events-none opacity-60' : ''}`}>
                      <UploadSimple className="mr-2 h-4 w-4" />{uploadingMusic ? 'Uploading music...' : config.music.custom_audio_url ? 'Replace uploaded music' : 'Upload your own music'}
                      <input type="file" accept="audio/mpeg,audio/wav,audio/ogg,audio/mp4,audio/aac,.mp3,.wav,.ogg,.m4a,.aac" className="sr-only" onChange={uploadInviteMusic} disabled={uploadingMusic} />
                    </label>
                    <p className="text-xs leading-5 text-muted-foreground">MP3, WAV, OGG, M4A or AAC, up to 20 MB. Guests always have an on-screen pause control.</p>
                  </>
                )}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </Card>
            </div>

            <div className={`shrink-0 ${mobileEditorCategory === 'style' ? '' : 'hidden xl:block'}`}>
            <Card className="overflow-hidden rounded-2xl border-border/80 shadow-sm">
              <CardHeader className="border-b bg-muted/35 pb-4">
                <CardTitle>Mobile layout &amp; artwork</CardTitle>
                <CardDescription>Every invitation uses a focused mobile canvas, including when guests open it on desktop.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Background focus</Label>
                    <Select value={config.layout.background_position} onValueChange={(value) => updateConfig('layout.background_position', value)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{['center', 'top', 'bottom', 'left', 'right'].map((value) => <SelectItem key={value} value={value}>{formatSelectLabel(value)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Opening height</Label>
                    <Select value={config.layout.mobile_hero_height} onValueChange={(value) => updateConfig('layout.mobile_hero_height', value)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="compact">Compact</SelectItem><SelectItem value="tall">Tall</SelectItem><SelectItem value="full">Full Screen</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2"><div className="flex justify-between text-xs"><Label>Image overlay</Label><span className="text-muted-foreground">{Math.round(config.layout.overlay_strength * 100)}%</span></div><Slider value={[config.layout.overlay_strength]} min={0} max={1} step={0.05} onValueChange={([value]) => updateConfig('layout.overlay_strength', value)} /></div>
                {config.layout.frame_image_url && <div className="flex items-center justify-between gap-3 rounded-lg border p-3"><div><p className="text-sm font-medium">Decorative frame</p><p className="text-xs text-muted-foreground">Layer the template artwork over the hero image.</p></div><Switch checked={config.layout.show_frame} onCheckedChange={(checked) => updateConfig('layout.show_frame', checked)} /></div>}
                {config.customization_mode === 'advanced' && (
                  <>
                    <div className="space-y-1.5"><Label>Frame image URL</Label><Input value={config.layout.frame_image_url || ''} onChange={(event) => updateConfig('layout.frame_image_url', event.target.value || null)} placeholder="https://… or /assets/…" /></div>
                    <div className="space-y-1.5"><Label>Section decoration URL</Label><Input value={config.layout.decorative_image_url || ''} onChange={(event) => updateConfig('layout.decorative_image_url', event.target.value || null)} placeholder="Transparent floral or ornamental artwork" /></div>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-1.5"><Label>Section canvas</Label><Select value={config.layout.section_style} onValueChange={(value) => updateConfig('layout.section_style', value)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="default">Default</SelectItem><SelectItem value="botanical_paper">Botanical Paper</SelectItem><SelectItem value="temple_courtyard">Temple Courtyard</SelectItem><SelectItem value="tapestry_folio">Royal Tapestry Folio</SelectItem><SelectItem value="river_paper">River Paper</SelectItem></SelectContent></Select></div>
                        <div className="space-y-1.5"><Label>Section transitions</Label><Select value={config.layout.section_divider} onValueChange={(value) => updateConfig('layout.section_divider', value)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">Seamless</SelectItem><SelectItem value="botanical">Torn Paper Panels</SelectItem><SelectItem value="temple_steps">Temple Steps</SelectItem><SelectItem value="woven_band">Woven Bands</SelectItem><SelectItem value="waterline">River Waterlines</SelectItem></SelectContent></Select></div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
            </div>

            {selectedSection && (
              <div className={`order-2 shrink-0 xl:order-none ${mobileOpeningEditorOnly ? 'hidden xl:block' : mobileEditorCategory === 'content' ? '' : 'hidden xl:block'}`}>
              <div>
              <SectionInsertButton placement="before" onClick={() => setSectionInsertRequest({ anchorId: selectedSection.id, placement: 'before' })} />
              </div>
              <Card ref={sectionEditorRef} className="overflow-hidden rounded-2xl border-border/80 shadow-sm">
                <CardHeader className="border-b bg-muted/35 pb-4">
                  <div className="flex items-start justify-between gap-3">
                    <div><CardTitle>Edit {selectedDefinition.label}</CardTitle><CardDescription className="mt-1">Blank fields continue using live event details.</CardDescription></div>
                    <div className="flex items-center gap-1">
                      <Button type="button" variant="ghost" size="icon" onClick={() => moveSection(selectedSection.id, -1)} disabled={visibleSections.findIndex((section) => section.id === selectedSection.id) <= 0} aria-label={`Move ${selectedDefinition.label} up`}><ArrowUp className="h-4 w-4" /></Button>
                      <Button type="button" variant="ghost" size="icon" onClick={() => moveSection(selectedSection.id, 1)} disabled={visibleSections.findIndex((section) => section.id === selectedSection.id) >= visibleSections.length - 1} aria-label={`Move ${selectedDefinition.label} down`}><ArrowDown className="h-4 w-4" /></Button>
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeSectionFromInvite(selectedSection)} disabled={visibleSections.length <= 1} aria-label={`Remove ${selectedDefinition.label} from invite`}><Trash className="h-4 w-4 text-destructive" /></Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-4">
                  <div className="space-y-1.5"><Label>Headline</Label><Input value={selectedSection.overrides?.headline || ''} onChange={(event) => updateSection(selectedSection.id, (item) => ({ ...item, overrides: { ...item.overrides, headline: event.target.value } }))} placeholder="Use event details" /></div>
                  <div className="space-y-1.5"><Label>Supporting message</Label><Textarea value={selectedSection.overrides?.body || ''} onChange={(event) => updateSection(selectedSection.id, (item) => ({ ...item, overrides: { ...item.overrides, body: event.target.value } }))} placeholder="Use event details" rows={3} /></div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5"><Label>Layout</Label><Select value={selectedSection.variant} onValueChange={(value) => updateSection(selectedSection.id, (item) => ({ ...item, variant: value }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{getSectionVariants(selectedSection.type, currentEventType).map((variant) => <SelectItem key={variant} value={variant}>{formatSelectLabel(variant)}</SelectItem>)}</SelectContent></Select></div>
                    {config.customization_mode === 'advanced' && ['hero', 'story', 'gallery'].includes(selectedSection.type) && <div className="space-y-1.5"><Label>Content source</Label><Select value={selectedSection.source_mode === 'custom' ? 'custom' : 'event'} onValueChange={(value) => updateSection(selectedSection.id, (item) => ({ ...item, source_mode: value }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="event">Event Details + Your Edits</SelectItem><SelectItem value="custom">Custom Only</SelectItem></SelectContent></Select><p className="text-xs text-muted-foreground">Event details stay live unless you choose custom only. Your headline and message edits always take priority.</p></div>}
                  </div>
                  </div>
                  {selectedSection.type === 'hero' && (
                    <InvitePhotoPicker
                      title="Hero photo"
                      description="Choose an event photo or upload one specifically for this invitation."
                      photos={availableInvitePhotos}
                      selectedUrls={selectedSection.overrides?.image_url ? [selectedSection.overrides.image_url] : []}
                      uploading={uploadingInviteImageSlot === 'hero'}
                      onToggle={(url) => updateSection(selectedSection.id, (item) => ({ ...item, overrides: { ...item.overrides, image_url: item.overrides?.image_url === url ? null : url } }))}
                      onClear={() => updateSection(selectedSection.id, (item) => ({ ...item, overrides: { ...item.overrides, image_url: null } }))}
                      onUpload={(event) => uploadInviteImage(event, 'hero', (url) => updateSection(selectedSection.id, (item) => ({ ...item, overrides: { ...item.overrides, image_url: url } })))}
                    />
                  )}
                  {selectedSection.type === 'rsvp' && selectedSection.variant === 'wax_seal' && (
                    <InvitePhotoPicker
                      title="Couple portrait"
                      description="Shown as the closing image beneath the RSVP moment."
                      photos={availableInvitePhotos}
                      selectedUrls={selectedSection.overrides?.couple_photo_url ? [selectedSection.overrides.couple_photo_url] : []}
                      uploading={uploadingInviteImageSlot === 'couple'}
                      onToggle={(url) => updateSection(selectedSection.id, (item) => ({ ...item, overrides: { ...item.overrides, couple_photo_url: item.overrides?.couple_photo_url === url ? null : url } }))}
                      onClear={() => updateSection(selectedSection.id, (item) => ({ ...item, overrides: { ...item.overrides, couple_photo_url: null } }))}
                      onUpload={(event) => uploadInviteImage(event, 'couple', (url) => updateSection(selectedSection.id, (item) => ({ ...item, overrides: { ...item.overrides, couple_photo_url: url } })))}
                    />
                  )}
                  {selectedSection.type === 'itinerary' && currentEventType !== 'birthday' && (
                    <div className="space-y-3 rounded-lg border p-3">
                      <div className="flex items-center justify-between gap-3"><div><p className="text-sm font-medium">Include full itinerary</p><p className="text-xs text-muted-foreground">Automatically keeps all itinerary items in sync.</p></div><Switch checked={selectedSection.settings?.include_all_items === true} onCheckedChange={(checked) => updateSection(selectedSection.id, (item) => ({ ...item, settings: { ...item.settings, include_all_items: checked } }))} /></div>
                      {!selectedSection.settings?.include_all_items && (currentEvent.itinerary || []).map((item) => {
                        const selectedIds = selectedSection.settings?.selected_item_ids || [];
                        const checked = selectedIds.includes(item.id);
                        return <label key={item.id} className="flex items-center gap-3 text-sm"><input type="checkbox" checked={checked} onChange={() => updateSection(selectedSection.id, (current) => ({ ...current, settings: { ...current.settings, selected_item_ids: checked ? selectedIds.filter((id) => id !== item.id) : [...selectedIds, item.id] } }))} />{item.name}</label>;
                      })}
                    </div>
                  )}
                  {selectedSection.type === 'gallery' && (() => {
                    const galleryImages = (currentEvent.wedding_photos_preview || []).filter((url) => typeof url === 'string' && url);
                    const selectedImages = selectedSection.settings?.selected_image_urls || [];
                    const uploadedImages = selectedSection.overrides?.image_urls || [];
                    const includeAllImages = selectedSection.settings?.include_all_images !== false;
                    return (
                      <div className="space-y-3 rounded-lg border p-3">
                        <div className="flex items-center justify-between gap-3">
                          <div><p className="text-sm font-medium">Include all gallery images</p><p className="text-xs text-muted-foreground">New event photos will automatically appear in this invite.</p></div>
                          <Switch
                            checked={includeAllImages}
                            onCheckedChange={(checked) => updateSection(selectedSection.id, (item) => ({
                              ...item,
                              settings: {
                                ...item.settings,
                                include_all_images: checked,
                                selected_image_urls: checked
                                  ? (item.settings?.selected_image_urls || [])
                                  : ((item.settings?.selected_image_urls || []).length ? item.settings.selected_image_urls : galleryImages.slice(0, 6)),
                              },
                            }))}
                          />
                        </div>
                        {!galleryImages.length && !uploadedImages.length && includeAllImages && <p className="text-xs leading-5 text-muted-foreground">Two labeled sample couple photos appear until you upload your own or add photos to the event.</p>}
                        {!includeAllImages && (galleryImages.length ? (
                          <div className="grid grid-cols-3 gap-2">
                            {galleryImages.map((url, index) => {
                              const checked = selectedImages.includes(url);
                              return (
                                <label key={`${url}-${index}`} className={`relative aspect-square cursor-pointer overflow-hidden rounded-lg border-2 transition-colors ${checked ? 'border-primary' : 'border-transparent opacity-55 hover:opacity-80'}`}>
                                  <img src={resolveMediaUrl(url)} alt={`Gallery option ${index + 1}`} className="h-full w-full object-cover" />
                                  <span className="absolute right-1.5 top-1.5 grid h-5 w-5 place-items-center rounded-full bg-white/90 shadow">
                                    <input
                                      type="checkbox"
                                      className="h-3.5 w-3.5 accent-primary"
                                      checked={checked}
                                      onChange={() => updateSection(selectedSection.id, (item) => ({
                                        ...item,
                                        settings: {
                                          ...item.settings,
                                          selected_image_urls: checked
                                            ? selectedImages.filter((image) => image !== url)
                                            : [...selectedImages, url],
                                        },
                                      }))}
                                    />
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        ) : <p className="text-xs leading-5 text-muted-foreground">Add photos to the event gallery first, then return here to choose them.</p>)}
                        {uploadedImages.length > 0 && (
                          <div>
                            <p className="mb-2 text-xs font-medium text-muted-foreground">Uploaded for this template</p>
                            <div className="grid grid-cols-3 gap-2">
                              {uploadedImages.map((url, index) => (
                                <div key={`${url}-${index}`} className="group relative aspect-square overflow-hidden rounded-lg border">
                                  <img src={resolveMediaUrl(url)} alt={`Uploaded gallery option ${index + 1}`} className="h-full w-full object-cover" />
                                  <button
                                    type="button"
                                    onClick={() => updateSection(selectedSection.id, (item) => ({ ...item, overrides: { ...item.overrides, image_urls: (item.overrides?.image_urls || []).filter((image) => image !== url) } }))}
                                    className="absolute right-1.5 top-1.5 grid h-6 w-6 place-items-center rounded-full bg-black/65 text-white opacity-90 transition-opacity hover:bg-destructive"
                                    aria-label={`Remove uploaded photo ${index + 1}`}
                                  >
                                    <Trash className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <label className={`inline-flex cursor-pointer items-center rounded-md border border-dashed px-3 py-2 text-xs font-medium transition-colors hover:bg-muted/40 ${uploadingInviteImageSlot === 'gallery' ? 'pointer-events-none opacity-60' : ''}`}>
                          <UploadSimple className="mr-1.5 h-4 w-4" />{uploadingInviteImageSlot === 'gallery' ? 'Uploading...' : 'Upload another gallery photo'}
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            className="sr-only"
                            disabled={uploadingInviteImageSlot === 'gallery'}
                            onChange={(event) => uploadInviteImage(event, 'gallery', (url) => updateSection(selectedSection.id, (item) => ({ ...item, overrides: { ...item.overrides, image_urls: [...(item.overrides?.image_urls || []), url] } })))}
                          />
                        </label>
                      </div>
                    );
                  })()}
                  {selectedSection.type === 'map' && (
                    <div className="space-y-2 rounded-lg border p-3">
                      {currentEventType === 'birthday' ? (
                        <p className="text-xs leading-5 text-muted-foreground">
                          Birthday venue is managed from Event details. Update it there and this map will use the same location.
                        </p>
                      ) : (
                        <>
                          <Label>Event location shown on the map</Label>
                          {(currentEvent.venues || []).length ? (
                            <Select
                              value={selectedSection.settings?.selected_venue_id ? String(selectedSection.settings.selected_venue_id) : 'auto'}
                              onValueChange={(value) => updateSection(selectedSection.id, (item) => ({
                                ...item,
                                settings: { ...item.settings, selected_venue_id: value === 'auto' ? null : value },
                              }))}
                            >
                              <SelectTrigger><SelectValue placeholder="Choose a venue" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="auto">First Event Location</SelectItem>
                                {(currentEvent.venues || []).map((venue, index) => {
                                  const value = venue.id ? String(venue.id) : `index:${index}`;
                                  const label = venue.name || venue.hall_name || venue.address || `Location ${index + 1}`;
                                  return <SelectItem key={value} value={value}>{label}</SelectItem>;
                                })}
                              </SelectContent>
                            </Select>
                          ) : <p className="text-xs leading-5 text-muted-foreground">Add locations to the event first, then return here to choose one.</p>}
                        </>
                      )}
                      <p className="text-xs leading-5 text-muted-foreground">The public invite will show the selected address and an interactive map.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
              <div>
              <SectionInsertButton placement="after" onClick={() => setSectionInsertRequest({ anchorId: selectedSection.id, placement: 'after' })} />
              </div>
              </div>
            )}

            <div className={`shrink-0 ${mobileEditorCategory === 'style' ? '' : 'hidden xl:block'}`}>
            <Card className="overflow-hidden rounded-2xl border-border/80 shadow-sm">
              <CardHeader className="border-b bg-muted/35 pb-4"><CardTitle>Look & motion</CardTitle><CardDescription>Make the atmosphere feel like your celebration.</CardDescription></CardHeader>
              <CardContent className="space-y-5">
                <div className="grid grid-cols-5 gap-3">
                  {[
                    ['primary_color', 'Primary'], ['secondary_color', 'Secondary'], ['accent_color', 'Accent'], ['background_color', 'Background'], ['text_color', 'Text'],
                  ].map(([key, label]) => <label key={key} className="text-center text-[0.62rem] text-muted-foreground"><input type="color" value={config.theme[key]} onChange={(event) => updateConfig(`theme.${key}`, event.target.value)} className="h-9 w-full cursor-pointer rounded border bg-transparent p-0.5" aria-label={label} /><span className="mt-1 block">{label}</span></label>)}
                </div>
                 <div className="space-y-1.5"><Label>Hero atmosphere</Label><Select value={config.effects.hero_effect} onValueChange={(value) => updateConfig('effects.hero_effect', value)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{HERO_EFFECTS.map(([value, label]) => <SelectItem key={value} value={value}>{formatSelectLabel(label)}</SelectItem>)}</SelectContent></Select></div>
                {config.effects.hero_effect !== 'none' && <div className="space-y-2"><div className="flex justify-between text-xs"><Label>Effect intensity</Label><span className="text-muted-foreground">{Math.round(config.effects.intensity * 100)}%</span></div><Slider value={[config.effects.intensity]} min={0} max={2} step={0.05} onValueChange={([value]) => updateConfig('effects.intensity', value)} /></div>}
                {config.customization_mode === 'advanced' && (
                  <>
                    {config.effects.hero_effect !== 'none' && <div className="space-y-2"><div className="flex justify-between text-xs"><Label>Effect speed</Label><span className="text-muted-foreground">{Math.round(config.effects.speed * 100)}%</span></div><Slider value={[config.effects.speed]} min={0} max={1} step={0.05} onValueChange={([value]) => updateConfig('effects.speed', value)} /></div>}
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5"><Label>Heading font</Label><Select value={config.theme.heading_font} onValueChange={(value) => updateConfig('theme.heading_font', value)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{FONT_OPTIONS.map((font) => <SelectItem key={font} value={font}>{font}</SelectItem>)}</SelectContent></Select></div>
                      <div className="space-y-1.5"><Label>Body font</Label><Select value={config.theme.body_font} onValueChange={(value) => updateConfig('theme.body_font', value)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{FONT_OPTIONS.map((font) => <SelectItem key={font} value={font}>{font}</SelectItem>)}</SelectContent></Select></div>
                      <div className="space-y-1.5"><Label>Card surface</Label><Select value={config.theme.surface_style} onValueChange={(value) => updateConfig('theme.surface_style', value)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{['flat', 'soft', 'glass', 'paper'].map((value) => <SelectItem key={value} value={value}>{formatSelectLabel(value)}</SelectItem>)}</SelectContent></Select></div>
                      <div className="space-y-1.5"><Label>Card corners</Label><Select value={config.theme.corner_style} onValueChange={(value) => updateConfig('theme.corner_style', value)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{['square', 'soft', 'rounded', 'pill'].map((value) => <SelectItem key={value} value={value}>{formatSelectLabel(value)}</SelectItem>)}</SelectContent></Select></div>
                      <div className="space-y-1.5"><Label>Section reveal</Label><Select value={config.effects.scroll_reveal} onValueChange={(value) => updateConfig('effects.scroll_reveal', value)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{['none', 'fade', 'rise', 'scale'].map((value) => <SelectItem key={value} value={value}>{formatSelectLabel(value)}</SelectItem>)}</SelectContent></Select></div>
                    </div>
                    <p className="text-xs text-muted-foreground">Card surface and corners affect boxed layouts. Seamless layouts intentionally keep their theme artwork; scroll a section out and back in to preview its reveal.</p>
                    <div className="flex items-center justify-between gap-3 rounded-lg border p-3"><div><p className="text-sm font-medium">Parallax depth</p><p className="text-xs text-muted-foreground">Adds gentle depth while scrolling.</p></div><Switch checked={config.effects.parallax} onCheckedChange={(checked) => updateConfig('effects.parallax', checked)} /></div>
                    <div className="flex items-center justify-between gap-3 rounded-lg border p-3"><div><p className="text-sm font-medium">Interactive particles</p><p className="text-xs text-muted-foreground">Adds a soft glow when guests interact with the hero.</p></div><Switch checked={config.effects.pointer_interaction} onCheckedChange={(checked) => updateConfig('effects.pointer_interaction', checked)} /></div>
                  </>
                )}
              </CardContent>
            </Card>
            </div>

            {config.customization_mode === 'advanced' && (
              <Card className={`shrink-0 overflow-hidden rounded-2xl border-border/80 shadow-sm ${mobileEditorCategory === 'style' ? '' : 'hidden xl:block'}`}>
                <CardHeader className="border-b bg-muted/35 pb-4"><CardTitle>Sharing & guest experience</CardTitle><CardDescription>Control how the invite appears when shared.</CardDescription></CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-1.5"><Label>Share title</Label><Input value={config.seo.title || ''} maxLength={120} onChange={(event) => updateConfig('seo.title', event.target.value || null)} placeholder={currentEvent.name} /></div>
                  <div className="space-y-1.5"><Label>Share description</Label><Textarea value={config.seo.description || ''} maxLength={300} onChange={(event) => updateConfig('seo.description', event.target.value || null)} placeholder={`Join us for ${currentEvent.name}.`} /></div>
                </CardContent>
              </Card>
            )}
            <div className={`shrink-0 rounded-xl border border-dashed p-3 ${mobileEditorCategory === 'style' ? '' : 'hidden xl:block'}`}>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => updateConfig('customization_mode', config.customization_mode === 'advanced' ? 'guided' : 'advanced')}
              >
                {config.customization_mode === 'advanced' ? 'Hide advanced options' : 'More options'}
              </Button>
              <p className="mt-1 text-center text-xs text-muted-foreground">
                {config.customization_mode === 'advanced' ? 'Advanced design and sharing controls are visible.' : 'Fonts, detailed motion, section sources, and sharing metadata.'}
              </p>
            </div>
            </div>
          </div>

          <div className="hidden min-w-0 xl:block">
            <Card className="sticky top-4 overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-muted/30 px-4 py-3">
                <div><p className="text-sm font-semibold">Mobile invitation preview</p><p className="text-xs text-muted-foreground">Using current event details</p></div>
              </div>
              <CardContent className="bg-slate-100 p-2 sm:p-4">
                <div className="mx-auto max-h-[calc(100vh-10rem)] max-w-[430px] overflow-y-auto rounded-[1.75rem] bg-white shadow-xl">
                  <WebInviteRenderer config={config} eventData={currentEvent} preview initiallyOpened={editorPreviewInitiallyOpened} onVisibleSectionChange={handlePreviewVisibleSection} scrollToSection={previewScrollTarget} onAction={() => toast.info('Guests will be asked to sign in only when they use this action.')} />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      )}

      <Dialog open={Boolean(sectionInsertRequest)} onOpenChange={(open) => { if (!open) setSectionInsertRequest(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add a section {sectionInsertRequest?.placement}</DialogTitle>
            <DialogDescription>Choose what should appear {sectionInsertRequest?.placement} {selectedDefinition.label.toLowerCase()}. You can edit it immediately after adding it.</DialogDescription>
          </DialogHeader>
          <div className="grid max-h-[60vh] gap-2 overflow-y-auto py-2 sm:grid-cols-2">
            {addableSections.map((section) => {
              const definition = sectionDefinition(section.type);
              return (
                <button key={section.id} type="button" onClick={() => insertSection(section.id)} className="rounded-lg border p-4 text-left transition-colors hover:border-primary/50 hover:bg-primary/5">
                  <span className="flex items-center gap-2 text-sm font-semibold"><Plus className="h-4 w-4 text-primary" />{definition.label}</span>
                  <span className="mt-1.5 block text-xs leading-5 text-muted-foreground">{definition.description}</span>
                </button>
              );
            })}
            {config.customization_mode === 'advanced' && config.sections.length < 20 && (
              <button type="button" onClick={() => insertSection('custom')} className="rounded-lg border border-dashed p-4 text-left transition-colors hover:border-primary/50 hover:bg-primary/5">
                <span className="flex items-center gap-2 text-sm font-semibold"><Plus className="h-4 w-4 text-primary" />Custom section</span>
                <span className="mt-1.5 block text-xs leading-5 text-muted-foreground">Add a freeform message, poem, tradition, travel note, or anything unique to the celebration.</span>
              </button>
            )}
            {!addableSections.length && config.customization_mode !== 'advanced' && (
              <div className="col-span-full py-8 text-center text-sm text-muted-foreground">Every available section is already part of this invitation.</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(duplicateTemplatePrompt)}
        onOpenChange={(open) => {
          if (!open && !resolvingDuplicateTemplate) setDuplicateTemplatePrompt(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>This design is already in My templates</DialogTitle>
            <DialogDescription>
              {duplicateTemplatePrompt?.existingTemplate.name || 'This template'} already uses this library design. Replace it with your changes or keep both by saving a new copy.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-2 py-1 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => setDuplicateSaveMode('replace')}
              className={`rounded-xl border p-3 text-left transition-colors ${duplicateSaveMode === 'replace' ? 'border-primary bg-primary/5 ring-2 ring-primary/10' : 'hover:border-primary/40'}`}
            >
              <span className="block text-sm font-semibold">Replace existing</span>
              <span className="mt-1 block text-xs leading-5 text-muted-foreground">Update the saved template with these changes.</span>
            </button>
            <button
              type="button"
              onClick={() => setDuplicateSaveMode('copy')}
              className={`rounded-xl border p-3 text-left transition-colors ${duplicateSaveMode === 'copy' ? 'border-primary bg-primary/5 ring-2 ring-primary/10' : 'hover:border-primary/40'}`}
            >
              <span className="block text-sm font-semibold">Save a new copy</span>
              <span className="mt-1 block text-xs leading-5 text-muted-foreground">Keep the existing template unchanged.</span>
            </button>
          </div>

          <div className="space-y-2">
            <Label htmlFor="duplicate-template-name">Template name</Label>
            <Input
              id="duplicate-template-name"
              value={duplicateTemplateNames[duplicateSaveMode] || ''}
              onChange={(event) => setDuplicateTemplateNames((current) => ({
                ...current,
                [duplicateSaveMode]: event.target.value,
              }))}
              maxLength={80}
              autoFocus
            />
            <p className="text-xs leading-5 text-muted-foreground">
              {duplicateSaveMode === 'replace'
                ? duplicateTemplatePrompt?.existingTemplate.id === activeGuestTemplateId
                  ? 'This is the active invitation. Guests will see the replacement.'
                  : 'The existing saved template will keep its place in My templates.'
                : 'The new copy will be saved to My templates and will not become active automatically.'}
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDuplicateTemplatePrompt(null)} disabled={resolvingDuplicateTemplate}>Cancel</Button>
            <Button type="button" variant={duplicateSaveMode === 'replace' ? 'destructive' : 'default'} onClick={resolveDuplicateTemplate} disabled={resolvingDuplicateTemplate}>
              {resolvingDuplicateTemplate
                ? 'Saving...'
                : duplicateSaveMode === 'replace'
                  ? 'Replace template'
                  : 'Save copy'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={saveTemplateDialogOpen} onOpenChange={(open) => { if (!savingNamedTemplate) { setSaveTemplateDialogOpen(open); if (!open) setPendingTemplateSave(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Name your invitation</DialogTitle>
            <DialogDescription>Save this design under My templates. It receives its own public link and can be shared with selected guests.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="saved-invite-name">Template name</Label>
            <Input id="saved-invite-name" value={saveTemplateName} onChange={(event) => setSaveTemplateName(event.target.value)} maxLength={80} placeholder={saveTemplatePlaceholder} autoFocus />
            <p className="text-xs text-muted-foreground">Leave blank to use “{saveTemplatePlaceholder}”.</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setSaveTemplateDialogOpen(false)} disabled={savingNamedTemplate}>Cancel</Button>
            <Button type="button" onClick={createNamedTemplate} disabled={savingNamedTemplate}><BookmarkSimple className="mr-2 h-4 w-4" />{savingNamedTemplate ? 'Saving...' : 'Save to My templates'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(pendingShareAction)} onOpenChange={(open) => { if (!open) setPendingShareAction(null); }}>
        <AlertDialogContent>
          <button type="button" aria-label="Close" onClick={() => setPendingShareAction(null)} className="absolute right-4 top-4 z-30 rounded-sm p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"><X className="h-4 w-4" /></button>
          <AlertDialogHeader>
            <AlertDialogTitle>Some invitation details are missing</AlertDialogTitle>
            <AlertDialogDescription>
              These details may show as generic text or sample content when guests open the invite. You can update them before sharing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="max-h-60 list-disc space-y-1 overflow-y-auto pl-5 text-sm text-foreground">
            {pendingShareAction?.missing.map((item) => <li key={item}>{item}</li>)}
          </ul>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={customizePendingTemplate}>Edit invitation</AlertDialogCancel>
            <AlertDialogAction onClick={(event) => { event.preventDefault(); continueShareAction(); }}>Continue Anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={Boolean(shareTemplate)}
        onOpenChange={(open) => {
          if (!open && !sendingShare) {
            setShareTemplate(null);
            setShareGuestSearch('');
            setSelectedShareGuestIds([]);
          }
        }}
      >
        <DialogContent className="flex max-h-[88vh] max-w-2xl flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>Share {shareTemplate?.name || 'invitation'}</DialogTitle>
            <DialogDescription>Select the guests who should receive this invite link in the wedding invitation WhatsApp message.</DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 space-y-3 overflow-hidden py-1">
            <Input
              value={shareGuestSearch}
              onChange={(event) => setShareGuestSearch(event.target.value)}
              placeholder="Search guests by name, phone or email"
              aria-label="Search guests"
            />

            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-muted/30 px-3 py-2 text-sm">
              <label className="flex cursor-pointer items-center gap-2 font-medium">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-primary"
                  checked={eligibleFilteredShareGuests.length > 0 && eligibleFilteredShareGuests.every((guest) => selectedShareGuestIds.includes(guest.id))}
                  onChange={(event) => toggleAllFilteredShareGuests(event.target.checked)}
                  disabled={!eligibleFilteredShareGuests.length || shareGuestsLoading}
                />
                Select all shown
              </label>
              <span className="text-xs text-muted-foreground">{selectedShareGuestIds.length} selected</span>
            </div>

            <div className="max-h-[46vh] overflow-y-auto rounded-lg border">
              {shareGuestsLoading && (
                <div className="grid min-h-48 place-items-center">
                  <div className="text-center"><div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary/20 border-t-primary" /><p className="mt-3 text-sm text-muted-foreground">Loading guests...</p></div>
                </div>
              )}
              {!shareGuestsLoading && filteredShareGuests.length === 0 && (
                <div className="px-5 py-12 text-center text-sm text-muted-foreground">{shareGuestSearch ? 'No guests match your search.' : 'No guests have been added to this event yet.'}</div>
              )}
              {!shareGuestsLoading && filteredShareGuests.map((guest) => {
                const eligible = Boolean(guest.phone);
                return (
                  <label key={guest.id} className={`flex items-center gap-3 border-b px-4 py-3 last:border-b-0 ${eligible ? 'cursor-pointer hover:bg-muted/35' : 'cursor-not-allowed opacity-55'}`}>
                    <input
                      type="checkbox"
                      className="h-4 w-4 shrink-0 accent-primary"
                      checked={selectedShareGuestIds.includes(guest.id)}
                      onChange={(event) => toggleShareGuest(guest.id, event.target.checked)}
                      disabled={!eligible}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{guestDisplayName(guest)}</span>
                      <span className="mt-0.5 block truncate text-xs text-muted-foreground">{guest.phone || 'No phone number — WhatsApp unavailable'}</span>
                    </span>
                    {guest.whatsapp_invite_status && <Badge variant="outline" className="shrink-0 text-[0.6rem] capitalize">{String(guest.whatsapp_invite_status).replace(/_/g, ' ')}</Badge>}
                  </label>
                );
              })}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShareTemplate(null)} disabled={sendingShare}>Cancel</Button>
            <Button type="button" onClick={sendSavedTemplateToGuests} disabled={sendingShare || !selectedShareGuestIds.length || shareGuestsLoading}>
              <ShareNetwork className="mr-2 h-4 w-4" />{sendingShare ? 'Sending...' : `Send to ${selectedShareGuestIds.length || 0} guest${selectedShareGuestIds.length === 1 ? '' : 's'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(pendingDeleteTemplate)} onOpenChange={(open) => { if (!open && !deletingSavedTemplate) setPendingDeleteTemplate(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {pendingDeleteTemplate?.name || 'this invitation'}?</AlertDialogTitle>
            <AlertDialogDescription>This removes it from My templates and disables its public invite link. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingSavedTemplate}>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={(event) => { event.preventDefault(); deleteSavedTemplate(); }} disabled={deletingSavedTemplate}>
              <Trash className="mr-2 h-4 w-4" />{deletingSavedTemplate ? 'Removing...' : 'Remove invitation'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={historyOpen} onOpenChange={(open) => { if (!restoringRevision) setHistoryOpen(open); }}>
        <DialogContent className="h-[92vh] max-w-6xl grid-rows-[auto_minmax(0,1fr)] gap-0 overflow-hidden p-0">
          <DialogHeader className="border-b px-6 pb-4 pt-6">
            <DialogTitle className="flex items-center gap-2"><ClockCounterClockwise className="h-5 w-5 text-primary" />Invite version history</DialogTitle>
            <DialogDescription>Preview saved design snapshots and restore one safely. Restoring creates a new editable version while preserving the existing history.</DialogDescription>
          </DialogHeader>

          {historyLoading && (
            <div className="grid min-h-[520px] place-items-center p-8">
              <div className="text-center"><div className="mx-auto h-9 w-9 animate-spin rounded-full border-4 border-primary/20 border-t-primary" /><p className="mt-3 text-sm text-muted-foreground">Loading saved versions...</p></div>
            </div>
          )}

          {!historyLoading && historyError && (
            <div className="grid min-h-[420px] place-items-center p-8 text-center">
              <div className="max-w-md"><Warning className="mx-auto h-9 w-9 text-destructive" /><p className="mt-3 font-medium">Version history is unavailable</p><p className="mt-2 text-sm leading-6 text-muted-foreground">{historyError}</p><Button type="button" variant="outline" className="mt-5" onClick={fetchRevisionHistory}>Try again</Button></div>
            </div>
          )}

          {!historyLoading && !historyError && !revisions.length && (
            <div className="grid min-h-[420px] place-items-center p-8 text-center">
              <div><ClockCounterClockwise className="mx-auto h-9 w-9 text-muted-foreground" /><p className="mt-3 font-medium">No saved versions yet</p><p className="mt-1 text-sm text-muted-foreground">Make and save an invite change to create the first snapshot.</p></div>
            </div>
          )}

          {!historyLoading && !historyError && revisions.length > 0 && (
            <div className="grid min-h-0 overflow-y-auto lg:grid-cols-[310px_minmax(0,1fr)] lg:overflow-hidden">
              <div className="max-h-60 overflow-y-auto border-b bg-muted/20 p-3 lg:max-h-none lg:border-b-0 lg:border-r">
                <p className="px-2 pb-3 text-xs leading-5 text-muted-foreground">Up to the 50 most recent saved versions are shown.</p>
                <div className="space-y-2">
                  {revisions.map((revision) => {
                    const selected = revision.revision === selectedRevisionNumber;
                    const isCurrent = revision.revision === invite?.revision;
                    const enabledCount = (revision.config?.sections || []).filter((section) => section.enabled).length;
                    const colors = revision.config?.theme || {};
                    return (
                      <button key={revision.revision} type="button" onClick={() => setSelectedRevisionNumber(revision.revision)} className={`w-full rounded-xl border p-3 text-left transition-colors ${selected ? 'border-primary bg-primary/5 ring-2 ring-primary/10' : 'bg-background hover:bg-muted/50'}`}>
                        <div className="flex items-start justify-between gap-2"><p className="text-sm font-semibold">Version {revision.revision}</p>{isCurrent && <Badge className="text-[0.6rem]">Current</Badge>}</div>
                        <p className="mt-1 text-xs text-muted-foreground">{readableRevisionDate(revision.created_at)}</p>
                        <div className="mt-3 flex items-center justify-between gap-3">
                          <div className="flex gap-1">{[colors.primary_color, colors.secondary_color, colors.background_color].filter(Boolean).map((color, index) => <span key={`${color}-${index}`} className="h-4 w-4 rounded-full border border-black/10" style={{ background: color }} />)}</div>
                          <span className="text-[0.65rem] text-muted-foreground">{enabledCount} section{enabledCount === 1 ? '' : 's'}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {selectedRevision && (
                <div className="flex min-h-[500px] flex-col lg:min-h-0">
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-3">
                    <div>
                      <p className="font-semibold">Version {selectedRevision.revision}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">{readableRevisionDate(selectedRevision.created_at)} | {WEB_INVITE_TEMPLATES.find((item) => item.id === selectedRevision.config?.template_id)?.name || 'Custom design'}</p>
                    </div>
                    <div className="text-right text-xs text-muted-foreground"><p>{(selectedRevision.config?.sections || []).filter((section) => section.enabled).length} visible sections</p><p className="mt-0.5 capitalize">{(selectedRevision.config?.effects?.hero_effect || 'no').replace(/_/g, ' ')} hero effect</p></div>
                  </div>
                  <div className="min-h-0 flex-1 overflow-y-auto bg-slate-100 p-3 sm:p-5">
                    <div className="mx-auto max-w-3xl overflow-hidden rounded-lg bg-white shadow-xl">
                      <WebInviteRenderer config={selectedRevision.config} eventData={currentEvent} preview onAction={() => {}} />
                    </div>
                  </div>
                  <div className="flex flex-col gap-3 border-t bg-background px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <p className="max-w-xl text-xs leading-5 text-muted-foreground">Only the design configuration is restored. Your current share address and live event details remain unchanged.</p>
                    <div className="flex gap-2"><Button type="button" variant="outline" onClick={() => setHistoryOpen(false)}>Close</Button><Button type="button" onClick={() => { setHistoryOpen(false); setPendingRestoreRevision(selectedRevision); }} disabled={selectedRevision.revision === invite?.revision || restoringRevision}><ArrowCounterClockwise className="mr-2 h-4 w-4" />{selectedRevision.revision === invite?.revision ? 'Current version' : 'Restore this version'}</Button></div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(pendingRestoreRevision)} onOpenChange={(open) => { if (!open && !restoringRevision) { setPendingRestoreRevision(null); setHistoryOpen(true); } }}>
        <AlertDialogContent className="z-[90]">
          <AlertDialogHeader>
            <AlertDialogTitle>Restore version {pendingRestoreRevision?.revision}?</AlertDialogTitle>
            <AlertDialogDescription>
              This design will become a new editable version, preserving both your current changes and the selected snapshot in history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restoringRevision}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(event) => { event.preventDefault(); restoreRevision(); }} disabled={restoringRevision}>
              <ArrowCounterClockwise className="mr-2 h-4 w-4" />{restoringRevision ? 'Restoring...' : 'Restore as new draft'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={aiStudioOpen} onOpenChange={setAiStudioOpen}>
        <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkle className="h-5 w-5 text-violet-600" weight="fill" />AI Creative Studio</DialogTitle>
            <DialogDescription>Describe what makes this celebration yours. AI returns editable art direction, copy, palette, motion and an optional hero background.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-5 lg:grid-cols-[minmax(0,0.82fr)_minmax(0,1.18fr)]">
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="ai-invite-brief">Your creative brief</Label>
                <Textarea
                  id="ai-invite-brief"
                  value={aiPrompt}
                  onChange={(event) => setAiPrompt(event.target.value)}
                  maxLength={1500}
                  rows={7}
                  placeholder="For example: a winter wedding in the mountains, midnight blue and candlelight, handwritten warmth, subtle snowfall, with small details inspired by our love of stargazing..."
                />
                <p className="text-right text-[0.65rem] text-muted-foreground">{aiPrompt.length}/1500</p>
              </div>

              <div className="space-y-2">
                <Label>Creative range</Label>
                <div className="grid gap-2">
                  {AI_CREATIVITY_OPTIONS.map(([value, label, description]) => (
                    <button key={value} type="button" onClick={() => setAiCreativity(value)} className={`rounded-lg border p-3 text-left transition-colors ${aiCreativity === value ? 'border-violet-500 bg-violet-50' : 'hover:bg-muted/40'}`}>
                      <span className="text-sm font-medium">{label}</span>
                      <span className="ml-2 text-xs text-muted-foreground">{description}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 rounded-lg border bg-background p-3">
                <div><p className="text-sm font-medium">Keep my current palette</p><p className="text-xs text-muted-foreground">Explore typography, motion and copy without changing colors.</p></div>
                <Switch checked={aiPreservePalette} onCheckedChange={setAiPreservePalette} />
              </div>

              <AIPremiumButton type="button" featureName="AI invite concepts" usageCategory={AI_USAGE_LUNA_TEXT} className="w-full" onClick={generateAIConcepts} disabled={aiGenerating || aiPrompt.trim().length < 3}>
                <MagicWand className="mr-2 h-4 w-4" />{aiGenerating ? 'Creating three directions...' : aiConcepts.length ? 'Create new directions' : 'Create three directions'}
              </AIPremiumButton>
              <p className="text-xs leading-5 text-muted-foreground">Only event and invite design details are used. Guest records are never sent for generation.</p>
            </div>

            <div className="min-w-0 space-y-4">
              {!aiConcepts.length && (
                <div className="grid min-h-[320px] place-items-center rounded-xl border border-dashed bg-muted/20 p-8 text-center">
                  <div><Sparkle className="mx-auto h-9 w-9 text-violet-400" /><p className="mt-3 font-medium">Your directions will appear here</p><p className="mt-1 max-w-sm text-sm leading-6 text-muted-foreground">Try a shared memory, season, regional craft, favorite place, music, color, flower or atmosphere.</p></div>
                </div>
              )}

              {aiConcepts.length > 0 && (
                <>
                  <div className="grid gap-3 sm:grid-cols-3">
                    {aiConcepts.map((concept) => {
                      const selected = concept.id === selectedAIConceptId;
                      return (
                        <button key={concept.id} type="button" onClick={() => { setSelectedAIConceptId(concept.id); setAiHeroAsset(null); }} className={`rounded-xl border p-3 text-left transition-all ${selected ? 'border-violet-500 bg-violet-50 ring-2 ring-violet-500/15' : 'hover:-translate-y-0.5 hover:shadow-sm'}`}>
                          <div className="flex gap-1">{[concept.theme.primary_color, concept.theme.secondary_color, concept.theme.accent_color, concept.theme.background_color].map((color, index) => <span key={`${color}-${index}`} className="h-5 flex-1 rounded-sm border border-black/5" style={{ background: color }} />)}</div>
                          <p className="mt-3 text-sm font-semibold">{concept.name}</p>
                          <p className="mt-1 line-clamp-3 text-xs leading-5 text-muted-foreground">{concept.tagline}</p>
                          {selected && <Badge className="mt-3 bg-violet-600">Selected</Badge>}
                        </button>
                      );
                    })}
                  </div>

                  {selectedAIConcept && (
                    <div className="space-y-4 rounded-xl border bg-background p-4">
                      <div>
                        <div className="flex flex-wrap items-start justify-between gap-2"><div><p className="text-lg font-semibold">{selectedAIConcept.name}</p><p className="mt-1 text-sm text-muted-foreground">{selectedAIConcept.tagline}</p></div><Badge variant="outline">Editable direction</Badge></div>
                        <p className="mt-3 text-sm leading-6 text-muted-foreground">{selectedAIConcept.rationale}</p>
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {(selectedAIConcept.signature_details || []).map((detail, index) => <div key={`${detail}-${index}`} className="rounded-lg bg-muted/45 px-3 py-2 text-xs leading-5">{detail}</div>)}
                      </div>
                      <Button type="button" onClick={applyAIConcept} className="w-full bg-violet-600 text-white hover:bg-violet-700"><Check className="mr-2 h-4 w-4" />Apply this direction</Button>

                      <div className="border-t pt-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                          <div className="flex-1 space-y-1.5"><Label>Hero art style</Label><Select value={aiHeroStyle} onValueChange={(value) => { setAiHeroStyle(value); setAiHeroAsset(null); }}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{AI_HERO_STYLES.map(([value, label]) => <SelectItem key={value} value={value}>{formatSelectLabel(label)}</SelectItem>)}</SelectContent></Select></div>
                          <AIPremiumButton type="button" featureName="AI hero artwork" usageCategory={AI_USAGE_IMAGE_GENERATION} variant="outline" onClick={generateAIHero} disabled={aiHeroGenerating}><ImageSquare className="mr-2 h-4 w-4" />{aiHeroGenerating ? 'Creating artwork...' : 'Create hero artwork'}</AIPremiumButton>
                        </div>
                        <p className="mt-2 text-xs leading-5 text-muted-foreground">Creates decorative wide artwork without names or dates, so live invite text stays readable and editable.</p>
                      </div>

                      {aiHeroAsset?.url && (
                        <div className="overflow-hidden rounded-xl border">
                          <img src={resolveMediaUrl(aiHeroAsset.url)} alt={aiHeroAsset.alt_text || ''} className="aspect-[3/2] w-full object-cover" />
                          <div className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between"><p className="text-xs text-muted-foreground">Review the artwork before placing it in your invite.</p><Button type="button" size="sm" onClick={useAIHero}><Check className="mr-2 h-4 w-4" />Use as hero</Button></div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
